// Editor de Questões — migração do legado `editor/index.html` pro painel
// novo do professor. Mantém os padrões visuais que já existem na prova/
// estudo (mesmas cores, mesmo layout de card, mesmas seções de filtros)
// pra reduzir desorientação visual entre os papéis.
//
// Por que View e não modal: o editor precisa de espaço (lista lateral +
// área principal com até 6 grupos de campos por questão), e a edição é
// trabalho contínuo — quem está aqui está focado nisso. Modal traria
// constrangimentos de tamanho/scroll e dificultaria deeplink.
//
// Fluxo:
//   1) Sem `editingBankId` setado → tela "Escolha um banco" lista bancos
//      do professor (mesmos dados do BancosView, mas em formato seletor).
//   2) Com `editingBankId` setado → carrega payload completo via getBank,
//      renderiza sidebar de questões (com filtros) + área de edição da
//      questão atual.
//
// Modelo de save (v1): mantém o array de questões em memória, salva o
// payload INTEIRO via PATCH /api/teacher/banks ({ data: { questoes: [...] } }).
// É o mesmo modelo de "substituir conteúdo" do legado — simples e funciona.
// Otimização futura: endpoint de PATCH por questão.
//
// Tipos de questão suportados:
//   - "mc" (default): pergunta + opcoes[] + correta (1-indexed, 0 = anulada)
//   - "essay": pergunta + banca + genero + linhas_min/max + fragmentos +
//              direcionadores + gabarito_modelo + criterios + competencias +
//              nota_maxima

import { signal } from "@preact/signals";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  AlignLeft,
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Database,
  Eye,
  FileText,
  Hourglass,
  ImageIcon,
  Info,
  ListChecks,
  Loader2,
  Pencil,
  PenLine,
  Plus,
  Redo2,
  Save,
  Search,
  Table as TableIcon,
  Tag,
  Trash2,
  Undo2,
  X
} from "lucide-react";
import { Switch } from "@app/ui/components/switch";
import { QuestionRichField } from "@app/ui/question-rich-field";
import { flagEnabled } from "@app/ui/lib/feature-flags";
import { api, ApiError, type BankRow } from "../api";
import { editingBankId, subCrumbs } from "../App";
import { openConfirm } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

// ============================================================================
// Tipos
// ============================================================================

type QuestionKind = "mc" | "essay";

interface EssayFragment {
  label: string;
  textContent: string;
}

/** Forma de uma questão dentro do payload editado. Inclui campos comuns
 *  + específicos de cada tipo. Persistimos só os campos preenchidos
 *  (sanitização em `cleanQuestao`) pra não inchar o JSON.
 *
 *  Questões compostas (estilo "a) ... b) ... c) ..." sob o mesmo caso) usam
 *  o array `items[]`. Quando `items` está ausente ou tem 0/1 elementos, a
 *  questão se comporta como simples — todos os campos específicos de tipo
 *  (opcoes/correta/explicacao OU banca/genero/gabarito_modelo/criterios) vivem
 *  no nível raiz. Quando `items` tem 2+, cada item carrega seus próprios
 *  campos específicos e o nível raiz mantém apenas o que é COMPARTILHADO
 *  (caso, imagens, tabelas, fragmentos, enunciado disparador).
 */
interface EditQuestao {
  id: string;
  tipo?: QuestionKind;
  pergunta: string;
  caso?: string;
  // MC (campos do nível raiz; ignorados quando items.length >= 2)
  opcoes?: string[];
  correta?: number;
  explicacao?: string;
  // Metadados gerais
  tema?: string;
  subtema?: string;
  fonte?: string;
  arquivo_origem?: string;
  numero_origem?: string;
  dificuldade?: number;
  imagens?: Array<string | { url: string; legenda?: string }>;
  tabelas?: Array<{ titulo?: string; cabecalho: string[]; linhas: string[][]; nota?: string }>;
  // Essay (idem — campos de raiz só valem quando questão é simples)
  banca?: string;
  genero?: string;
  linhas_min?: number;
  linhas_max?: number;
  fragmentos?: EssayFragment[];
  direcionadores?: string;
  gabarito_modelo?: string;
  criterios?: string[];
  competencias?: string[];
  nota_maxima?: number;
  // Peso da questão (ou do item) para popular prova agendada.
  notaQuestao?: number;
  // Sub-itens (questão composta). Cada um é uma "mini questão" com seu
  // próprio tipo, pergunta, gabarito etc.; campos compartilhados (caso,
  // imagens, tabelas, fragmentos, enunciado disparador) ficam no pai.
  items?: EditItem[];
}

/** Sub-item de uma questão composta. Carrega só o que é específico do
 *  item — o caso/imagens/tabelas vivem no pai e a renderização junta. */
interface EditItem {
  id: string;
  label: string;                // "a", "b", "c", "i", "ii", "1", "2"…
  tipo?: QuestionKind;          // pode misturar (item a essay, item b mc)
  pergunta: string;             // comando específico do item
  // MC do item
  opcoes?: string[];
  correta?: number;
  explicacao?: string;
  // Essay do item
  banca?: string;
  genero?: string;
  linhas_min?: number;
  linhas_max?: number;
  direcionadores?: string;
  gabarito_modelo?: string;
  criterios?: string[];
  competencias?: string[];
  nota_maxima?: number;
  // Peso individual do item na prova agendada (a:0,4 + b:0,3 + c:0,3 etc.)
  notaQuestao?: number;
}

interface BankEditState {
  meta: BankRow;
  questoes: EditQuestao[];
  /** Snapshot original (pra detectar dirty). */
  originalJson: string;
  dirty: boolean;
  saving: boolean;
}

// ============================================================================
// State
// ============================================================================

const phase = signal<"empty" | "loading" | "ready" | "error">("empty");
const errMsg = signal<string | null>(null);
const state = signal<BankEditState | null>(null);
const currentIdx = signal<number>(0);
const filtroBusca = signal<string>("");
const filtroTipo = signal<"all" | "mc" | "essay">("all");
/** Modo do card da questão: editar (default) ou pré-visualizar exatamente
 *  como o aluno veria no Modo Estudo. Signal global pra preservar o modo
 *  quando o professor navega entre questões. */
const cardMode = signal<"edit" | "preview">("edit");

// ============================================================================
// Histórico (undo/redo) — Ctrl+Z e Ctrl+Y
// ============================================================================
//
// Cada mutação que altera o array `questoes` (add/remove/move/update item/...)
// empurra um snapshot completo do array no stack. Ctrl+Z/Y movem o cursor.
// Limitamos a 50 snapshots — depois disso descartamos os mais antigos.
// Snapshots são JSON-strings pra evitar referências compartilhadas que
// poderiam ser mutadas por engano em outro ponto.

const HISTORY_MAX = 50;
const historyStack = signal<string[]>([]);
const historyCursor = signal<number>(-1);

function pushHistory(snapshot: EditQuestao[]) {
  const s = JSON.stringify(snapshot);
  const stack = historyStack.value;
  const cursor = historyCursor.value;
  // Se o cursor estava no meio (usuário fez undo, agora faz nova mudança),
  // descartamos tudo depois do cursor — branch alternativa some.
  let next = cursor < stack.length - 1 ? stack.slice(0, cursor + 1) : stack.slice();
  // Não empurra se for idêntico ao topo (mutações que devolvem o mesmo
  // estado, ex.: clicar na mesma alternativa).
  if (next.length > 0 && next[next.length - 1] === s) return;
  next.push(s);
  if (next.length > HISTORY_MAX) {
    next = next.slice(next.length - HISTORY_MAX);
  }
  historyStack.value = next;
  historyCursor.value = next.length - 1;
}

function canUndo(): boolean {
  return historyCursor.value > 0;
}
function canRedo(): boolean {
  return historyCursor.value < historyStack.value.length - 1;
}

function undo() {
  if (!canUndo()) return;
  const s = state.value;
  if (!s) return;
  const target = historyCursor.value - 1;
  historyCursor.value = target;
  const snap: EditQuestao[] = JSON.parse(historyStack.value[target]);
  // Não chama markDirty/pushHistory daqui (senão entra em loop). Apenas
  // atualiza state direto e recalcula dirty contra originalJson.
  const currentJson = JSON.stringify(snap);
  state.value = { ...s, questoes: snap, dirty: currentJson !== s.originalJson };
}

function redo() {
  if (!canRedo()) return;
  const s = state.value;
  if (!s) return;
  const target = historyCursor.value + 1;
  historyCursor.value = target;
  const snap: EditQuestao[] = JSON.parse(historyStack.value[target]);
  const currentJson = JSON.stringify(snap);
  state.value = { ...s, questoes: snap, dirty: currentJson !== s.originalJson };
}

function resetHistory(initialSnapshot: EditQuestao[]) {
  historyStack.value = [JSON.stringify(initialSnapshot)];
  historyCursor.value = 0;
}

// ============================================================================
// Load / Save
// ============================================================================

async function loadBank(bankId: string) {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.getBank(bankId);
    const raw = r.bank.payload;
    const rawQs: any[] =
      raw && typeof raw === "object" && Array.isArray(raw.questoes)
        ? raw.questoes
        : raw && typeof raw === "object" && Array.isArray(raw.questions)
          ? raw.questions
          : [];
    const questoes = rawQs.map((q, i) => normalizeIncoming(q, i));
    const snapshot = JSON.stringify(questoes);
    state.value = {
      meta: r.bank,
      questoes,
      originalJson: snapshot,
      dirty: false,
      saving: false
    };
    currentIdx.value = 0;
    phase.value = "ready";
    // Reseta histórico — undo/redo só vai até o estado de abertura.
    resetHistory(questoes);
    // Crumb com nome do banco — limpa ao sair via exitEditor.
    subCrumbs.value = [{ label: r.bank.name }];
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

/** Aceita os campos cruz do banco legado (`alternativas` em vez de `opcoes`,
 *  por exemplo) e devolve uma EditQuestao no formato canônico. */
function normalizeIncoming(raw: any, idx: number): EditQuestao {
  const q: EditQuestao = {
    id: typeof raw.id === "string" && raw.id ? raw.id : `q-${idx + 1}`,
    pergunta: typeof raw.pergunta === "string" ? raw.pergunta : ""
  };
  if (raw.tipo === "essay" || raw.tipo === "mc") q.tipo = raw.tipo;
  if (typeof raw.caso === "string") q.caso = raw.caso;
  // MC
  const opc = Array.isArray(raw.opcoes)
    ? raw.opcoes
    : Array.isArray(raw.alternativas)
      ? raw.alternativas
      : null;
  if (opc) q.opcoes = opc.map((x: unknown) => String(x ?? ""));
  if (typeof raw.correta === "number") q.correta = raw.correta;
  if (typeof raw.explicacao === "string") q.explicacao = raw.explicacao;
  // Meta
  if (typeof raw.tema === "string") q.tema = raw.tema;
  if (typeof raw.subtema === "string") q.subtema = raw.subtema;
  if (typeof raw.fonte === "string") q.fonte = raw.fonte;
  if (typeof raw.arquivo_origem === "string") q.arquivo_origem = raw.arquivo_origem;
  if (raw.numero_origem != null) q.numero_origem = String(raw.numero_origem);
  if (typeof raw.dificuldade === "number") q.dificuldade = raw.dificuldade;
  if (Array.isArray(raw.imagens)) q.imagens = raw.imagens;
  if (Array.isArray(raw.tabelas)) q.tabelas = raw.tabelas;
  // Essay
  if (typeof raw.banca === "string") q.banca = raw.banca;
  if (typeof raw.genero === "string") q.genero = raw.genero;
  if (typeof raw.linhas_min === "number") q.linhas_min = raw.linhas_min;
  if (typeof raw.linhas_max === "number") q.linhas_max = raw.linhas_max;
  if (Array.isArray(raw.fragmentos)) {
    q.fragmentos = raw.fragmentos
      .filter((f: any) => f && typeof f === "object")
      .map((f: any) => ({
        label: typeof f.label === "string" ? f.label : "",
        textContent: typeof f.textContent === "string" ? f.textContent : ""
      }));
  }
  if (typeof raw.direcionadores === "string") q.direcionadores = raw.direcionadores;
  if (typeof raw.gabarito_modelo === "string") q.gabarito_modelo = raw.gabarito_modelo;
  if (Array.isArray(raw.criterios)) q.criterios = raw.criterios.map(String);
  if (Array.isArray(raw.competencias)) q.competencias = raw.competencias.map(String);
  if (typeof raw.nota_maxima === "number") q.nota_maxima = raw.nota_maxima;
  if (typeof raw.notaQuestao === "number") q.notaQuestao = raw.notaQuestao;
  if (Array.isArray(raw.items) && raw.items.length > 0) {
    q.items = raw.items
      .filter((it: any) => it && typeof it === "object")
      .map((it: any, i: number) => normalizeIncomingItem(it, i));
  }
  return q;
}

function normalizeIncomingItem(raw: any, idx: number): EditItem {
  const out: EditItem = {
    id: typeof raw.id === "string" && raw.id ? raw.id : `item-${idx + 1}`,
    label: typeof raw.label === "string" && raw.label
      ? raw.label
      : String.fromCharCode(97 + idx), // "a", "b", "c"…
    pergunta: typeof raw.pergunta === "string" ? raw.pergunta : ""
  };
  if (raw.tipo === "essay" || raw.tipo === "mc") out.tipo = raw.tipo;
  // MC
  const opc = Array.isArray(raw.opcoes)
    ? raw.opcoes
    : Array.isArray(raw.alternativas)
      ? raw.alternativas
      : null;
  if (opc) out.opcoes = opc.map((x: unknown) => String(x ?? ""));
  if (typeof raw.correta === "number") out.correta = raw.correta;
  if (typeof raw.explicacao === "string") out.explicacao = raw.explicacao;
  // Essay
  if (typeof raw.banca === "string") out.banca = raw.banca;
  if (typeof raw.genero === "string") out.genero = raw.genero;
  if (typeof raw.linhas_min === "number") out.linhas_min = raw.linhas_min;
  if (typeof raw.linhas_max === "number") out.linhas_max = raw.linhas_max;
  if (typeof raw.direcionadores === "string") out.direcionadores = raw.direcionadores;
  if (typeof raw.gabarito_modelo === "string") out.gabarito_modelo = raw.gabarito_modelo;
  if (Array.isArray(raw.criterios)) out.criterios = raw.criterios.map(String);
  if (Array.isArray(raw.competencias)) out.competencias = raw.competencias.map(String);
  if (typeof raw.nota_maxima === "number") out.nota_maxima = raw.nota_maxima;
  if (typeof raw.notaQuestao === "number") out.notaQuestao = raw.notaQuestao;
  return out;
}

/** Remove campos vazios/undefined antes de enviar pro servidor. */
function cleanQuestao(q: EditQuestao): Record<string, unknown> {
  const out: Record<string, unknown> = { id: q.id, pergunta: q.pergunta };
  const isComposta = Array.isArray(q.items) && q.items.length >= 2;
  const kind = q.tipo === "essay" ? "essay" : "mc";
  if (q.tipo) out.tipo = q.tipo;
  if (q.caso) out.caso = q.caso;
  if (q.tema) out.tema = q.tema;
  if (q.subtema) out.subtema = q.subtema;
  if (q.fonte) out.fonte = q.fonte;
  if (q.arquivo_origem) out.arquivo_origem = q.arquivo_origem;
  if (q.numero_origem) out.numero_origem = q.numero_origem;
  if (typeof q.dificuldade === "number") out.dificuldade = q.dificuldade;
  if (q.imagens && q.imagens.length > 0) out.imagens = q.imagens;
  if (q.tabelas && q.tabelas.length > 0) out.tabelas = q.tabelas;
  if (typeof q.notaQuestao === "number") out.notaQuestao = q.notaQuestao;
  // Fragmentos sempre são compartilhados (motivadores da proposta inteira),
  // logo ficam no nível raiz mesmo em questão composta.
  if (q.fragmentos && q.fragmentos.length > 0) out.fragmentos = q.fragmentos;
  if (q.direcionadores) out.direcionadores = q.direcionadores;

  if (isComposta) {
    // Questão composta: campos específicos de tipo (opcoes, correta, gabarito,
    // critérios) vão pra cada item. Raiz mantém só o que é compartilhado.
    out.items = q.items!.map(cleanItem);
  } else if (kind === "mc") {
    if (q.opcoes && q.opcoes.length > 0) out.opcoes = q.opcoes;
    if (typeof q.correta === "number") out.correta = q.correta;
    if (q.explicacao) out.explicacao = q.explicacao;
  } else {
    if (q.banca) out.banca = q.banca;
    if (q.genero) out.genero = q.genero;
    if (typeof q.linhas_min === "number") out.linhas_min = q.linhas_min;
    if (typeof q.linhas_max === "number") out.linhas_max = q.linhas_max;
    if (q.gabarito_modelo) out.gabarito_modelo = q.gabarito_modelo;
    if (q.criterios && q.criterios.length > 0) out.criterios = q.criterios;
    if (q.competencias && q.competencias.length > 0) out.competencias = q.competencias;
    if (typeof q.nota_maxima === "number") out.nota_maxima = q.nota_maxima;
  }
  return out;
}

function cleanItem(it: EditItem): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: it.id,
    label: it.label,
    pergunta: it.pergunta
  };
  const kind = it.tipo === "essay" ? "essay" : "mc";
  if (it.tipo) out.tipo = it.tipo;
  if (typeof it.notaQuestao === "number") out.notaQuestao = it.notaQuestao;
  if (kind === "mc") {
    if (it.opcoes && it.opcoes.length > 0) out.opcoes = it.opcoes;
    if (typeof it.correta === "number") out.correta = it.correta;
    if (it.explicacao) out.explicacao = it.explicacao;
  } else {
    if (it.banca) out.banca = it.banca;
    if (it.genero) out.genero = it.genero;
    if (typeof it.linhas_min === "number") out.linhas_min = it.linhas_min;
    if (typeof it.linhas_max === "number") out.linhas_max = it.linhas_max;
    if (it.direcionadores) out.direcionadores = it.direcionadores;
    if (it.gabarito_modelo) out.gabarito_modelo = it.gabarito_modelo;
    if (it.criterios && it.criterios.length > 0) out.criterios = it.criterios;
    if (it.competencias && it.competencias.length > 0) out.competencias = it.competencias;
    if (typeof it.nota_maxima === "number") out.nota_maxima = it.nota_maxima;
  }
  return out;
}

function markDirty() {
  const s = state.value;
  if (!s) return;
  const currentJson = JSON.stringify(s.questoes);
  state.value = { ...s, dirty: currentJson !== s.originalJson };
  // Cada mutação produtiva alimenta o histórico — ponto único de captura.
  pushHistory(s.questoes);
}

function updateQuestao(idx: number, patch: Partial<EditQuestao>) {
  const s = state.value;
  if (!s) return;
  const novas = s.questoes.map((q, i) => (i === idx ? { ...q, ...patch } : q));
  state.value = { ...s, questoes: novas };
  markDirty();
}

function addQuestao(kind: QuestionKind) {
  const s = state.value;
  if (!s) return;
  const nova: EditQuestao =
    kind === "essay"
      ? {
          id: `q-${Date.now()}`,
          tipo: "essay",
          pergunta: "",
          banca: "",
          genero: "Dissertativo-argumentativo",
          linhas_min: 7,
          linhas_max: 30,
          fragmentos: [],
          criterios: [],
          competencias: [],
          nota_maxima: 1000
        }
      : {
          id: `q-${Date.now()}`,
          tipo: "mc",
          pergunta: "",
          opcoes: ["", "", "", "", ""],
          correta: 1,
          dificuldade: 3
        };
  state.value = { ...s, questoes: [...s.questoes, nova] };
  currentIdx.value = s.questoes.length;
  markDirty();
}

async function duplicateQuestao(idx: number) {
  const s = state.value;
  if (!s) return;
  const orig = s.questoes[idx];
  if (!orig) return;
  const copy: EditQuestao = { ...orig, id: `q-${Date.now()}` };
  const novas = [...s.questoes];
  novas.splice(idx + 1, 0, copy);
  state.value = { ...s, questoes: novas };
  currentIdx.value = idx + 1;
  markDirty();
}

async function removeQuestao(idx: number) {
  const s = state.value;
  if (!s) return;
  const ok = await openConfirm({
    title: "Remover questão?",
    message:
      "A questão será removida do banco. A alteração só fica definitiva quando você salvar.",
    confirmLabel: "Remover",
    danger: true
  });
  if (!ok) return;
  const novas = s.questoes.filter((_, i) => i !== idx);
  state.value = { ...s, questoes: novas };
  currentIdx.value = Math.max(0, Math.min(currentIdx.value, novas.length - 1));
  markDirty();
}

// ============================================================================
// Helpers de items (questão composta)
// ============================================================================

/** Copia os campos do nível raiz pra um item[0] e adiciona um item[1] vazio.
 *  Cria a estrutura "composta" preservando o que o usuário já escreveu. */
function promoteToComposite(idx: number) {
  const s = state.value;
  if (!s) return;
  const q = s.questoes[idx];
  if (!q) return;
  if (Array.isArray(q.items) && q.items.length >= 2) return;
  const kind = q.tipo === "essay" ? "essay" : "mc";
  const itemA: EditItem = {
    id: `item-${Date.now()}-a`,
    label: "a",
    pergunta: q.pergunta || "",
    tipo: kind,
    notaQuestao: q.notaQuestao
  };
  if (kind === "mc") {
    if (q.opcoes && q.opcoes.length > 0) itemA.opcoes = q.opcoes;
    if (typeof q.correta === "number") itemA.correta = q.correta;
    if (q.explicacao) itemA.explicacao = q.explicacao;
  } else {
    if (q.banca) itemA.banca = q.banca;
    if (q.genero) itemA.genero = q.genero;
    if (typeof q.linhas_min === "number") itemA.linhas_min = q.linhas_min;
    if (typeof q.linhas_max === "number") itemA.linhas_max = q.linhas_max;
    if (q.direcionadores) itemA.direcionadores = q.direcionadores;
    if (q.gabarito_modelo) itemA.gabarito_modelo = q.gabarito_modelo;
    if (q.criterios) itemA.criterios = q.criterios;
    if (q.competencias) itemA.competencias = q.competencias;
    if (typeof q.nota_maxima === "number") itemA.nota_maxima = q.nota_maxima;
  }
  const itemB: EditItem = {
    id: `item-${Date.now()}-b`,
    label: "b",
    pergunta: "",
    tipo: kind,
    ...(kind === "mc"
      ? { opcoes: ["", "", "", "", ""], correta: 1 }
      : {
          banca: "",
          genero: "",
          linhas_min: 0,
          linhas_max: 0,
          criterios: [],
          competencias: [],
          nota_maxima: 10
        })
  };
  // Limpa o pai dos campos de tipo (que migraram pros items) e mantém o
  // que é compartilhado (caso/imagens/tabelas/fragmentos/tema/fonte).
  // Pergunta vira "enunciado disparador" — pode até ficar vazio.
  const novas = s.questoes.map((qq, i) =>
    i === idx
      ? {
          ...qq,
          pergunta: "Com base no contexto acima, responda:",
          // limpa campos de tipo do pai
          opcoes: undefined,
          correta: undefined,
          explicacao: undefined,
          banca: undefined,
          genero: undefined,
          linhas_min: undefined,
          linhas_max: undefined,
          direcionadores: undefined,
          gabarito_modelo: undefined,
          criterios: undefined,
          competencias: undefined,
          nota_maxima: undefined,
          items: [itemA, itemB]
        }
      : qq
  );
  state.value = { ...s, questoes: novas };
  markDirty();
}

/** Reverte uma questão composta para simples (copia items[0] de volta pro
 *  pai e remove items[]). Só faz sentido com 1 item restante. */
function demoteToSimple(idx: number) {
  const s = state.value;
  if (!s) return;
  const q = s.questoes[idx];
  if (!q || !Array.isArray(q.items) || q.items.length !== 1) return;
  const only = q.items[0];
  const novas = s.questoes.map((qq, i) =>
    i === idx
      ? {
          ...qq,
          pergunta: only.pergunta || qq.pergunta || "",
          tipo: only.tipo ?? qq.tipo,
          notaQuestao: only.notaQuestao ?? qq.notaQuestao,
          opcoes: only.opcoes,
          correta: only.correta,
          explicacao: only.explicacao,
          banca: only.banca,
          genero: only.genero,
          linhas_min: only.linhas_min,
          linhas_max: only.linhas_max,
          direcionadores: only.direcionadores,
          gabarito_modelo: only.gabarito_modelo,
          criterios: only.criterios,
          competencias: only.competencias,
          nota_maxima: only.nota_maxima,
          items: undefined
        }
      : qq
  );
  state.value = { ...s, questoes: novas };
  markDirty();
}

function nextItemLabel(items: EditItem[]): string {
  // Detecta o estilo do label do último item (a/b/c, i/ii/iii, 1/2/3) e
  // sugere o próximo na mesma família. Fallback: letras minúsculas.
  const last = items[items.length - 1]?.label ?? "";
  // Letras minúsculas single
  if (/^[a-z]$/.test(last)) {
    return String.fromCharCode(last.charCodeAt(0) + 1);
  }
  // Romanos minúsculos
  const romMap: Record<string, string> = {
    i: "ii", ii: "iii", iii: "iv", iv: "v", v: "vi", vi: "vii", vii: "viii"
  };
  if (last in romMap) return romMap[last];
  // Números
  if (/^\d+$/.test(last)) return String(Number(last) + 1);
  // Padrão: próxima letra após "a"
  return String.fromCharCode(97 + items.length);
}

function addItem(qIdx: number) {
  const s = state.value;
  if (!s) return;
  const q = s.questoes[qIdx];
  if (!q) return;
  const items = q.items ?? [];
  const novoItem: EditItem = {
    id: `item-${Date.now()}`,
    label: nextItemLabel(items),
    pergunta: "",
    tipo: "essay",
    criterios: [],
    competencias: [],
    nota_maxima: 10
  };
  updateQuestao(qIdx, { items: [...items, novoItem] });
}

function updateItem(qIdx: number, itemIdx: number, patch: Partial<EditItem>) {
  const s = state.value;
  if (!s) return;
  const q = s.questoes[qIdx];
  if (!q || !q.items) return;
  const novosItems = q.items.map((it, j) => (j === itemIdx ? { ...it, ...patch } : it));
  updateQuestao(qIdx, { items: novosItems });
}

async function removeItem(qIdx: number, itemIdx: number) {
  const s = state.value;
  if (!s) return;
  const q = s.questoes[qIdx];
  if (!q || !q.items) return;
  const ok = await openConfirm({
    title: `Remover item "${q.items[itemIdx]?.label ?? "?"}"?`,
    message:
      "O item será removido da questão. Os outros itens permanecem. Se sobrar só 1 item, a questão volta a ser simples automaticamente.",
    confirmLabel: "Remover item",
    danger: true
  });
  if (!ok) return;
  const novosItems = q.items.filter((_, j) => j !== itemIdx);
  updateQuestao(qIdx, { items: novosItems.length > 0 ? novosItems : undefined });
}

function moveItem(qIdx: number, itemIdx: number, dir: -1 | 1) {
  const s = state.value;
  if (!s) return;
  const q = s.questoes[qIdx];
  if (!q || !q.items) return;
  const target = itemIdx + dir;
  if (target < 0 || target >= q.items.length) return;
  const novos = [...q.items];
  [novos[itemIdx], novos[target]] = [novos[target], novos[itemIdx]];
  updateQuestao(qIdx, { items: novos });
}

function changeItemKind(qIdx: number, itemIdx: number, k: QuestionKind) {
  const s = state.value;
  if (!s) return;
  const q = s.questoes[qIdx];
  if (!q || !q.items) return;
  const it = q.items[itemIdx];
  if (!it) return;
  const curKind = it.tipo === "essay" ? "essay" : "mc";
  if (k === curKind) return;
  if (k === "essay") {
    updateItem(qIdx, itemIdx, {
      tipo: "essay",
      opcoes: undefined,
      correta: undefined,
      explicacao: undefined,
      banca: it.banca ?? "",
      genero: it.genero ?? "",
      linhas_min: it.linhas_min ?? 0,
      linhas_max: it.linhas_max ?? 0,
      criterios: it.criterios ?? [],
      competencias: it.competencias ?? [],
      nota_maxima: it.nota_maxima ?? 10
    });
  } else {
    updateItem(qIdx, itemIdx, {
      tipo: "mc",
      opcoes: it.opcoes && it.opcoes.length > 0 ? it.opcoes : ["", "", "", "", ""],
      correta: it.correta ?? 1
    });
  }
}

function moveQuestao(idx: number, dir: -1 | 1) {
  const s = state.value;
  if (!s) return;
  const target = idx + dir;
  if (target < 0 || target >= s.questoes.length) return;
  const novas = [...s.questoes];
  [novas[idx], novas[target]] = [novas[target], novas[idx]];
  state.value = { ...s, questoes: novas };
  currentIdx.value = target;
  markDirty();
}

async function saveBank() {
  const s = state.value;
  if (!s) return;
  state.value = { ...s, saving: true };
  try {
    const payload = { questoes: s.questoes.map(cleanQuestao) };
    await api.patchBank(s.meta.id, { data: payload });
    const snapshot = JSON.stringify(s.questoes);
    state.value = {
      ...state.value!,
      saving: false,
      dirty: false,
      originalJson: snapshot
    };
  } catch (e) {
    state.value = { ...state.value!, saving: false };
    errMsg.value = friendlyError(e);
  }
}

async function exitEditor(opts: { skipDirtyCheck?: boolean } = {}) {
  const s = state.value;
  if (!opts.skipDirtyCheck && s?.dirty) {
    const ok = await openConfirm({
      title: "Descartar alterações?",
      message:
        "Você tem mudanças não salvas neste banco. Se sair agora, elas serão perdidas.",
      confirmLabel: "Descartar e sair",
      danger: true
    });
    if (!ok) return;
  }
  editingBankId.value = null;
  state.value = null;
  phase.value = "empty";
  // Limpa subCrumbs do banco e histórico — entrar em outro banco reabre tudo.
  subCrumbs.value = [];
  historyStack.value = [];
  historyCursor.value = -1;
}

// ============================================================================
// View raiz
// ============================================================================

export function QuestionEditorView() {
  const bankId = editingBankId.value;

  useEffect(() => {
    if (bankId && state.value?.meta.id !== bankId) {
      void loadBank(bankId);
    }
    if (!bankId && phase.value !== "empty") {
      phase.value = "empty";
      state.value = null;
    }
  }, [bankId]);

  if (!bankId) return <BankPicker />;
  if (phase.value === "loading") return <EditorLoading />;
  if (phase.value === "error") return <EditorError />;
  if (phase.value === "ready" && state.value) return <EditorReady />;
  return null;
}

// ============================================================================
// Bank picker — quando entra pelo menu sem banco selecionado
// ============================================================================

function BankPicker() {
  const [banks, setBanks] = useState<BankRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.listBanks();
        setBanks(r.banks);
      } catch (e) {
        setErr(friendlyError(e));
      }
    })();
  }, []);

  async function createNew() {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.createBank({
        name: "Novo banco",
        description: null,
        data: { questoes: [] },
        tags: []
      });
      editingBankId.value = r.bank.id;
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  if (err)
    return (
      <div class="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-1.5 inline size-4" />
        {err}
      </div>
    );
  if (banks === null)
    return (
      <p class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando seus bancos…
      </p>
    );

  const ativos = banks.filter((b) => !b.archivedAt);

  return (
    <div class="space-y-5">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="text-2xl font-bold tracking-tight">Editor de Questões</h1>
          <p class="text-sm text-muted-foreground">
            Escolha um banco existente pra editar suas questões — ou crie um do zero.
          </p>
        </div>
        <button
          type="button"
          onClick={createNew}
          disabled={busy}
          class="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-bold text-white hover:brightness-110 disabled:opacity-60"
        >
          {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
          Novo banco em branco
        </button>
      </header>

      {ativos.length === 0 ? (
        <div class="rounded-md border border-dashed border-border px-6 py-12 text-center">
          <Database className="mx-auto mb-3 size-8 text-muted-foreground" />
          <p class="text-sm text-muted-foreground">
            Você ainda não tem bancos. Clique em <strong>Novo banco em branco</strong>{" "}
            pra começar.
          </p>
        </div>
      ) : (
        <ul class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {ativos.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                onClick={() => (editingBankId.value = b.id)}
                class="group flex w-full flex-col items-start gap-2 rounded-md border border-border bg-card/60 p-4 text-left transition-colors hover:border-primary/50 hover:bg-card"
              >
                <div class="flex w-full items-start gap-2">
                  <Database className="mt-0.5 size-4 shrink-0 text-primary" />
                  <h3 class="flex-1 truncate font-bold leading-tight">{b.name}</h3>
                </div>
                {b.description && (
                  <p class="line-clamp-2 text-xs text-muted-foreground">{b.description}</p>
                )}
                <div class="mt-auto flex w-full flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span>
                    {b.questionCount} {b.questionCount === 1 ? "questão" : "questões"}
                  </span>
                  <BankKindCounts b={b} />
                  <span aria-hidden="true" class="ml-auto">v{b.version}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Badges com contagem ME/DISS por banco (na lista de seleção do editor).
// Renderiza só os tipos com count > 0; banco vazio não mostra badge.
function BankKindCounts({ b }: { b: BankRow }) {
  const c = b.questionKindCounts;
  if (!c) return null;
  if (c.mc === 0 && c.essay === 0) return null;
  return (
    <span class="flex items-center gap-1">
      {c.mc > 0 && (
        <span
          class="rounded-md bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300"
          title={`${c.mc} de múltipla escolha`}
        >
          ME {c.mc}
        </span>
      )}
      {c.essay > 0 && (
        <span
          class="rounded-md bg-violet-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300"
          title={`${c.essay} dissertativa${c.essay === 1 ? "" : "s"}`}
        >
          DISS {c.essay}
        </span>
      )}
    </span>
  );
}

// ============================================================================
// Loading / Error
// ============================================================================

function EditorLoading() {
  return (
    <p class="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" /> Carregando banco para edição…
    </p>
  );
}

function EditorError() {
  return (
    <div class="space-y-3 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
      <AlertCircle className="mr-1.5 inline size-4" />
      {errMsg.value ?? "Erro desconhecido."}
      <div>
        <button
          type="button"
          onClick={() => void exitEditor({ skipDirtyCheck: true })}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
        >
          Voltar
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Editor pronto: header + sidebar + área principal
// ============================================================================

function EditorReady() {
  const s = state.value!;
  const filteredIdxs = useFilteredIndexes(s.questoes);
  const cur = s.questoes[currentIdx.value];

  // Atalhos globais Ctrl+Z (undo), Ctrl+Y / Ctrl+Shift+Z (redo). Não dispara
  // quando o foco está em input/textarea — esses widgets têm seu próprio
  // undo nativo, e desviar isso confunde digitação.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey)) return;
      const active = document.activeElement as HTMLElement | null;
      const insideField =
        active &&
        (active.tagName === "INPUT" ||
          active.tagName === "TEXTAREA" ||
          (active as HTMLElement).isContentEditable);
      if (insideField) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((key === "y") || (key === "z" && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div class="flex flex-col gap-4">
      <EditorHeader bank={s.meta} dirty={s.dirty} saving={s.saving} total={s.questoes.length} />
      <div class="grid gap-4 lg:grid-cols-[20rem_1fr]">
        <QuestionListSidebar
          questoes={s.questoes}
          filteredIdxs={filteredIdxs}
          currentIdx={currentIdx.value}
          onPick={(i) => (currentIdx.value = i)}
        />
        <div class="min-w-0">
          {cur ? (
            <QuestionEditCard q={cur} idx={currentIdx.value} total={s.questoes.length} />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </div>
  );
}

function useFilteredIndexes(qs: EditQuestao[]): number[] {
  const busca = filtroBusca.value.trim().toLowerCase();
  const tipo = filtroTipo.value;
  return useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i < qs.length; i++) {
      const q = qs[i];
      const kind = q.tipo === "essay" ? "essay" : "mc";
      if (tipo !== "all" && kind !== tipo) continue;
      if (busca) {
        const hay = [q.pergunta, q.caso ?? "", ...(q.opcoes ?? []), q.tema ?? "", q.banca ?? ""]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(busca)) continue;
      }
      out.push(i);
    }
    return out;
  }, [qs, busca, tipo]);
}

function EmptyState() {
  return (
    <div class="rounded-md border border-dashed border-border bg-card/40 px-6 py-16 text-center">
      <Pencil className="mx-auto mb-3 size-8 text-muted-foreground/60" />
      <p class="text-sm font-bold">Banco vazio</p>
      <p class="mt-1 text-xs text-muted-foreground">
        Use os botões <strong>+ ME</strong> ou <strong>+ Dissertativa</strong> na lista lateral
        pra criar a primeira questão.
      </p>
    </div>
  );
}

// ============================================================================
// Header — voltar, nome do banco, save, contagem
// ============================================================================

function EditorHeader({
  bank,
  dirty,
  saving,
  total
}: {
  bank: BankRow;
  dirty: boolean;
  saving: boolean;
  total: number;
}) {
  return (
    <header class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card/60 p-3 md:p-4">
      <button
        type="button"
        onClick={() => void exitEditor()}
        title="Voltar para Bancos"
        class="inline-flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background hover:bg-accent"
      >
        <ArrowLeft className="size-4" />
      </button>
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <h1 class="truncate text-base font-bold tracking-tight md:text-lg">{bank.name}</h1>
          <span class="shrink-0 rounded-md border border-border bg-background px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            v{bank.version}
          </span>
        </div>
        <p class="truncate text-xs text-muted-foreground">
          {total} {total === 1 ? "questão" : "questões"}
          {dirty && (
            <span class="ml-2 inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
              <span class="size-1.5 rounded-full bg-amber-500" />
              alterações não salvas
            </span>
          )}
        </p>
      </div>
      {/* Undo / Redo — mesmos atalhos do editor de texto (Ctrl+Z / Ctrl+Y).
          Histórico vai até 50 passos pra trás e termina na abertura do banco. */}
      <UndoRedoControls />
      <button
        type="button"
        onClick={() => void saveBank()}
        disabled={!dirty || saving}
        class="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-bold text-white hover:brightness-110 disabled:opacity-50"
      >
        {saving ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Save className="size-4" />
        )}
        {saving ? "Salvando…" : "Salvar banco"}
      </button>
    </header>
  );
}

function UndoRedoControls() {
  const undoable = canUndo();
  const redoable = canRedo();
  const passos = historyCursor.value; // quantos passos pra trás dá pra ir
  return (
    <div class="flex items-center gap-1">
      <button
        type="button"
        onClick={undo}
        disabled={!undoable}
        title={`Desfazer (Ctrl+Z)${undoable ? ` — ${passos} ${passos === 1 ? "passo" : "passos"} no histórico` : ""}`}
        aria-label="Desfazer"
        class="inline-flex size-9 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent disabled:opacity-30"
      >
        <Undo2 className="size-4" />
      </button>
      <button
        type="button"
        onClick={redo}
        disabled={!redoable}
        title="Refazer (Ctrl+Y ou Ctrl+Shift+Z)"
        aria-label="Refazer"
        class="inline-flex size-9 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent disabled:opacity-30"
      >
        <Redo2 className="size-4" />
      </button>
    </div>
  );
}

// ============================================================================
// Sidebar — lista de questões com busca + filtro tipo
// ============================================================================

function QuestionListSidebar({
  questoes,
  filteredIdxs,
  currentIdx,
  onPick
}: {
  questoes: EditQuestao[];
  filteredIdxs: number[];
  currentIdx: number;
  onPick: (idx: number) => void;
}) {
  return (
    <aside class="flex max-h-[calc(100vh-12rem)] flex-col gap-2 rounded-md border border-border bg-card/40 p-3 lg:sticky lg:top-4">
      <div class="space-y-2">
        <div class="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={filtroBusca.value}
            onInput={(e) => (filtroBusca.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar questão…"
            class="w-full rounded-md border border-border bg-background py-1.5 pl-7 pr-2 text-xs outline-none focus:border-primary"
          />
        </div>
        <div class="flex items-center gap-1 text-[10px]">
          <FilterPill
            label="Todos"
            active={filtroTipo.value === "all"}
            onClick={() => (filtroTipo.value = "all")}
          />
          <FilterPill
            label="ME"
            active={filtroTipo.value === "mc"}
            onClick={() => (filtroTipo.value = "mc")}
          />
          <FilterPill
            label="Diss"
            active={filtroTipo.value === "essay"}
            onClick={() => (filtroTipo.value = "essay")}
          />
        </div>
        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={() => addQuestao("mc")}
            class="flex-1 rounded-md border border-blue-500/40 bg-blue-500/5 px-2 py-1 text-[11px] font-bold text-blue-700 hover:bg-blue-500/10 dark:text-blue-300"
          >
            <Plus className="mr-0.5 inline size-3" /> ME
          </button>
          <button
            type="button"
            onClick={() => addQuestao("essay")}
            class="flex-1 rounded-md border border-violet-500/40 bg-violet-500/5 px-2 py-1 text-[11px] font-bold text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
          >
            <Plus className="mr-0.5 inline size-3" /> Dissertativa
          </button>
        </div>
      </div>

      <ul class="-mr-1 flex-1 space-y-0.5 overflow-y-auto pr-1">
        {filteredIdxs.length === 0 ? (
          <li class="px-2 py-4 text-center text-[11px] text-muted-foreground">
            Nenhuma questão bate com o filtro.
          </li>
        ) : (
          filteredIdxs.map((idx) => (
            <SidebarRow
              key={questoes[idx].id}
              q={questoes[idx]}
              idx={idx}
              active={idx === currentIdx}
              onPick={() => onPick(idx)}
            />
          ))
        )}
      </ul>
    </aside>
  );
}

function FilterPill({
  label,
  active,
  onClick
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`rounded-md border px-2 py-0.5 font-bold uppercase tracking-wider transition-colors ${
        active
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-background text-muted-foreground hover:bg-accent"
      }`}
    >
      {label}
    </button>
  );
}

function SidebarRow({
  q,
  idx,
  active,
  onPick
}: {
  q: EditQuestao;
  idx: number;
  active: boolean;
  onPick: () => void;
}) {
  const composta = Array.isArray(q.items) && q.items.length >= 2;
  // Em questão composta, sinaliza misto (DISS + ME) baseado nos items.
  // Em simples, usa o tipo do nível raiz.
  const itemKinds = composta
    ? Array.from(new Set(q.items!.map((it) => (it.tipo === "essay" ? "essay" : "mc"))))
    : [q.tipo === "essay" ? "essay" : "mc"];
  const titulo = q.pergunta?.slice(0, 80) || "(sem enunciado)";
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        class={`flex w-full items-start gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${
          active
            ? "border-primary bg-primary/10"
            : "border-transparent hover:border-border hover:bg-accent/60"
        }`}
      >
        <span class="mt-0.5 flex shrink-0 flex-col gap-0.5">
          {itemKinds.includes("essay") && (
            <span class="rounded-md bg-violet-500/15 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              DISS
            </span>
          )}
          {itemKinds.includes("mc") && (
            <span class="rounded-md bg-blue-500/15 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300">
              ME
            </span>
          )}
        </span>
        <span class="min-w-0 flex-1">
          <span class="block truncate font-semibold">
            {idx + 1}. {titulo}
          </span>
          <span class="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            {q.tema && <span class="truncate">{q.tema}</span>}
            {composta && (
              <span class="shrink-0 rounded-md bg-accent px-1 py-0.5 font-bold text-foreground">
                {q.items!.length} itens
              </span>
            )}
            {/* Mostra peso total da questão na sidebar — em composta soma os
                pesos dos items; em simples usa o notaQuestao raiz. */}
            {pesoTotal(q) > 0 && (
              <span class="shrink-0 rounded-md bg-primary/10 px-1 py-0.5 font-bold text-primary">
                {formatPeso(pesoTotal(q))} pt
              </span>
            )}
          </span>
        </span>
      </button>
    </li>
  );
}

// ============================================================================
// Card de edição da questão atual
// ============================================================================

function QuestionEditCard({
  q,
  idx,
  total
}: {
  q: EditQuestao;
  idx: number;
  total: number;
}) {
  const kind = q.tipo === "essay" ? "essay" : "mc";

  function patch(p: Partial<EditQuestao>) {
    updateQuestao(idx, p);
  }

  function changeKind(k: QuestionKind) {
    if (k === kind) return;
    if (k === "essay") {
      // Conserva pergunta/caso/tema/etc.; zera campos MC; popula defaults essay.
      patch({
        tipo: "essay",
        opcoes: undefined,
        correta: undefined,
        explicacao: undefined,
        banca: q.banca ?? "",
        genero: q.genero ?? "Dissertativo-argumentativo",
        linhas_min: q.linhas_min ?? 7,
        linhas_max: q.linhas_max ?? 30,
        fragmentos: q.fragmentos ?? [],
        criterios: q.criterios ?? [],
        competencias: q.competencias ?? [],
        nota_maxima: q.nota_maxima ?? 1000
      });
    } else {
      patch({
        tipo: "mc",
        opcoes: q.opcoes && q.opcoes.length > 0 ? q.opcoes : ["", "", "", "", ""],
        correta: q.correta ?? 1
      });
    }
  }

  return (
    <article class="space-y-4 rounded-md border border-border bg-card p-5">
      {/* Toolbar topo: navegação + ações da questão */}
      <header class="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={() => moveQuestao(idx, -1)}
            disabled={idx === 0}
            title="Mover pra cima"
            class="inline-flex size-8 items-center justify-center rounded-md border border-border bg-background hover:bg-accent disabled:opacity-40"
          >
            <ChevronUp className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => moveQuestao(idx, 1)}
            disabled={idx === total - 1}
            title="Mover pra baixo"
            class="inline-flex size-8 items-center justify-center rounded-md border border-border bg-background hover:bg-accent disabled:opacity-40"
          >
            <ChevronDown className="size-4" />
          </button>
          <span class="ml-2 text-sm font-bold text-muted-foreground">
            Questão {idx + 1} de {total}
          </span>
        </div>
        <div class="flex items-center gap-1">
          <CardModeToggle />
          <KindToggle current={kind} onChange={changeKind} />
          <button
            type="button"
            onClick={() => duplicateQuestao(idx)}
            title="Duplicar questão"
            class="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-background px-2 text-xs font-medium hover:bg-accent"
          >
            <Copy className="size-3.5" /> Duplicar
          </button>
          <button
            type="button"
            onClick={() => void removeQuestao(idx)}
            title="Remover questão"
            class="inline-flex h-8 items-center gap-1 rounded-md border border-red-500/40 bg-red-500/5 px-2 text-xs font-medium text-red-600 hover:bg-red-500/10 dark:text-red-400"
          >
            <Trash2 className="size-3.5" /> Remover
          </button>
        </div>
      </header>

      {cardMode.value === "preview" ? (
        <QuestionPreview q={q} />
      ) : (
        <QuestionEditBody q={q} kind={kind} patch={patch} />
      )}
    </article>
  );
}

// Toggle Editar ↔ Pré-visualizar. Visual idêntico ao KindToggle pra manter
// uma única gramática de pílulas duais no header da questão.
function CardModeToggle() {
  const m = cardMode.value;
  return (
    <div class="inline-flex items-center overflow-hidden rounded-md border border-border bg-background text-[11px]">
      <button
        type="button"
        onClick={() => (cardMode.value = "edit")}
        title="Editar campos da questão"
        class={`h-8 px-2 font-bold inline-flex items-center gap-1 ${
          m === "edit"
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-accent"
        }`}
      >
        <Pencil className="size-3" /> Editar
      </button>
      <button
        type="button"
        onClick={() => (cardMode.value = "preview")}
        title="Ver como o aluno verá no Modo Estudo"
        class={`h-8 border-l border-border px-2 font-bold inline-flex items-center gap-1 ${
          m === "preview"
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-accent"
        }`}
      >
        <Eye className="size-3" /> Pré-visualizar
      </button>
    </div>
  );
}

// Corpo de edição extraído do QuestionEditCard pra deixar o toggle preview
// limpo: só troca de body, header fica intacto (toolbar de navegação +
// duplicar/remover + tipo + modo).
function QuestionEditBody({
  q,
  kind,
  patch
}: {
  q: EditQuestao;
  kind: QuestionKind;
  patch: (p: Partial<EditQuestao>) => void;
}) {
  return (
    <>
      {/* Metadados — tema, subtema, fonte, dificuldade, numero */}
      <Section title="Metadados">
        <div class="grid gap-3 sm:grid-cols-2">
          <Field label="Tema">
            <input
              type="text"
              value={q.tema ?? ""}
              onInput={(e) => patch({ tema: (e.target as HTMLInputElement).value })}
              class={inputClass}
              placeholder="Ex: Medicina, Português, Matemática"
            />
          </Field>
          <Field label="Subtema">
            <input
              type="text"
              value={q.subtema ?? ""}
              onInput={(e) => patch({ subtema: (e.target as HTMLInputElement).value })}
              class={inputClass}
              placeholder="Opcional"
            />
          </Field>
          <Field label="Fonte / banca">
            <input
              type="text"
              value={q.fonte ?? ""}
              onInput={(e) => patch({ fonte: (e.target as HTMLInputElement).value })}
              class={inputClass}
              placeholder="Ex: ENEM 2022, FUVEST, IME"
            />
          </Field>
          <Field label="Nº na prova original">
            <input
              type="text"
              value={q.numero_origem ?? ""}
              onInput={(e) =>
                patch({ numero_origem: (e.target as HTMLInputElement).value })
              }
              class={inputClass}
              placeholder="Ex: 12, Q3, P"
            />
          </Field>
          <Field label="Dificuldade (1–5)">
            <select
              title="Dificuldade da questão"
              value={String(q.dificuldade ?? 3)}
              onChange={(e) =>
                patch({ dificuldade: Number((e.target as HTMLSelectElement).value) })
              }
              class={inputClass}
            >
              <option value="1">1 — muito fácil</option>
              <option value="2">2 — fácil</option>
              <option value="3">3 — média</option>
              <option value="4">4 — difícil</option>
              <option value="5">5 — muito difícil</option>
            </select>
          </Field>
          <Field label="Arquivo de origem">
            <input
              type="text"
              value={q.arquivo_origem ?? ""}
              onInput={(e) =>
                patch({ arquivo_origem: (e.target as HTMLInputElement).value })
              }
              class={inputClass}
              placeholder="Ex: ENEM_2023.pdf"
            />
          </Field>
          <Field label="Nota da questão (peso)">
            <input
              type="number"
              title="Peso da questão pra popular nota quando virar prova agendada"
              placeholder="ex: 1.0"
              value={String(q.notaQuestao ?? "")}
              onInput={(e) => {
                const v = (e.target as HTMLInputElement).value;
                patch({ notaQuestao: v === "" ? undefined : Number(v) });
              }}
              min={0}
              max={1000}
              step="0.1"
              class={inputClass}
            />
          </Field>
        </div>
        <p class="mt-1 text-[11px] text-muted-foreground">
          O peso é usado como sugestão quando o professor cria uma prova agendada
          a partir deste banco — a tela de criação ainda permite ajuste manual.
        </p>
      </Section>

      {/* Caso / contexto */}
      <Section title="Situação / contexto (caso)">
        <MdField
          value={q.caso ?? ""}
          onChange={(v) => patch({ caso: v })}
          variant="block"
          rows={4}
          placeholder="Texto-base, caso clínico, dados de apoio…"
        />
      </Section>

      {/* Enunciado */}
      <Section title="Enunciado (pergunta)">
        <MdField
          value={q.pergunta}
          onChange={(v) => patch({ pergunta: v })}
          variant="block"
          rows={3}
          placeholder="Comando principal da questão"
        />
      </Section>

      {/* Imagens — gerenciamento simples por URL/base64 */}
      <ImagensSection q={q} onPatch={patch} />

      {/* Tabelas — grade real (linhas × colunas) */}
      <TabelasSection q={q} onPatch={patch} />

      {/* Quando a questão é composta (items.length >= 2), os campos de
          tipo vivem em cada item. Caso contrário, mostra MC ou Essay no
          nível raiz. Em ambos os casos, oferecemos botão pra converter. */}
      {isComposite(q) ? (
        <ItemsSection q={q} idx={getIdx(q)} />
      ) : (
        <>
          {kind === "mc" ? (
            <McFields q={q} onPatch={patch} />
          ) : (
            <EssayFields q={q} onPatch={patch} />
          )}
          <CompositeToggle q={q} idx={getIdx(q)} />
        </>
      )}
    </>
  );
}

function isComposite(q: EditQuestao): boolean {
  return Array.isArray(q.items) && q.items.length >= 2;
}

/** Soma dos pesos da questão. Em composta = soma dos pesos dos items.
 *  Em simples = `notaQuestao` raiz. Zero quando nada foi definido — sidebar
 *  esconde o badge nesse caso pra não poluir bancos legados. */
function pesoTotal(q: EditQuestao): number {
  if (Array.isArray(q.items) && q.items.length >= 2) {
    let s = 0;
    for (const it of q.items) {
      if (typeof it.notaQuestao === "number" && it.notaQuestao > 0) s += it.notaQuestao;
    }
    return s;
  }
  return typeof q.notaQuestao === "number" && q.notaQuestao > 0 ? q.notaQuestao : 0;
}

function formatPeso(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1).replace(".", ",");
}

function getIdx(q: EditQuestao): number {
  // Recupera índice da questão no array a partir do id. Usado pelos botões
  // de items que precisam saber qIdx.
  const s = state.value;
  if (!s) return -1;
  return s.questoes.findIndex((qq) => qq.id === q.id);
}

// ============================================================================
// Pré-visualização da questão — espelha o visual do Modo Estudo (mesmo
// QuestionCard renderizado no painel do aluno). Read-only: alternativas não
// clicam, dissertativa mostra placeholder de "escreva aqui" como o aluno vê
// antes de responder.
//
// Importante: tudo aqui usa as MESMAS classes Tailwind do `QuestionCard`
// em `apps/student-new/src/QuestionCard.tsx` pra ficarem visualmente
// idênticos. Quando a refatoração de `QuestionCard` pra `apps/ui` for feita
// (item pendente), este componente vira um wrapper.

function QuestionPreview({ q }: { q: EditQuestao }) {
  const isEssay = q.tipo === "essay";
  const anulada = q.correta === 0;
  return (
    <div class="space-y-4 rounded-md border-2 border-dashed border-primary/30 bg-background/40 p-5">
      <div class="-mt-2 mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-primary">
        <Eye className="size-3" /> Pré-visualização — como o aluno vê
      </div>

      {/* Badges (tema, fonte, dificuldade, situação, imagem, tabela, anulada) */}
      <div class="flex flex-wrap items-center gap-2 text-[11px]">
        {q.numero_origem && (
          <span class="inline-flex h-6 items-center rounded bg-accent px-2 font-mono leading-none text-muted-foreground">
            #{q.numero_origem}
          </span>
        )}
        {q.tema && (
          <span class="inline-flex h-6 items-center gap-1.5 rounded bg-primary/10 px-2 font-medium leading-none text-primary">
            <Tag width="1.3em" height="1.3em" strokeWidth={2.2} />
            {q.tema}
          </span>
        )}
        {q.fonte && (
          <span class="inline-flex h-6 items-center rounded bg-accent px-2 leading-none text-muted-foreground">
            {q.fonte}
          </span>
        )}
        {q.dificuldade != null && q.dificuldade >= 1 && (
          <PreviewDifficultyStars value={Number(q.dificuldade)} />
        )}
        {q.caso && (
          <span class="inline-flex h-6 items-center gap-1 rounded bg-sky-500/15 px-2 font-medium leading-none text-sky-600 dark:text-sky-400">
            <AlignLeft width="1.3em" height="1.3em" /> Situação
          </span>
        )}
        {q.imagens && q.imagens.length > 0 && (
          <span class="inline-flex h-6 items-center gap-1 rounded bg-violet-500/15 px-2 font-medium leading-none text-violet-600 dark:text-violet-400">
            <ImageIcon width="1.3em" height="1.3em" /> Imagem
          </span>
        )}
        {q.tabelas && q.tabelas.length > 0 && (
          <span class="inline-flex h-6 items-center gap-1 rounded bg-teal-500/15 px-2 font-medium leading-none text-teal-600 dark:text-teal-400">
            <TableIcon width="1.3em" height="1.3em" /> Tabela
          </span>
        )}
        {anulada && (
          <span class="inline-flex h-6 items-center rounded bg-amber-500/20 px-2 font-medium leading-none text-amber-600 dark:text-amber-400">
            Anulada
          </span>
        )}
      </div>

      {/* Situação */}
      {q.caso && (
        <div class="rounded-md border-l-4 border-sky-500/50 bg-sky-500/5 px-4 py-3 text-sm">
          <div class="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
            Situação
          </div>
          <div
            class="prose-question text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: previewBlock(q.caso) }}
          />
        </div>
      )}

      {/* Tabelas */}
      {Array.isArray(q.tabelas) && q.tabelas.length > 0 && (
        <div class="space-y-3">
          {q.tabelas.map((t, i) => (
            <PreviewTable key={i} t={t} />
          ))}
        </div>
      )}

      {/* Imagens */}
      {q.imagens && q.imagens.length > 0 && (
        <div
          class={`mx-auto grid gap-3 ${
            q.imagens.length === 1 ? "max-w-md" : "max-w-2xl sm:grid-cols-2"
          }`}
        >
          {q.imagens.map((it, i) => {
            const url = typeof it === "string" ? it : it.url;
            const legenda = typeof it === "string" ? "" : it.legenda ?? "";
            return (
              <figure
                key={i}
                class="overflow-hidden rounded-md border border-border bg-accent/20"
              >
                <img
                  src={url}
                  alt={legenda || `Imagem ${i + 1}`}
                  class="mx-auto block w-full"
                  loading="lazy"
                />
                {legenda && (
                  <figcaption class="border-t border-border bg-card/60 px-3 py-2 text-center text-xs text-muted-foreground">
                    {legenda}
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>
      )}

      {/* Enunciado */}
      <div
        class="prose-question text-base leading-relaxed"
        dangerouslySetInnerHTML={{ __html: previewBlock(q.pergunta || "(sem enunciado)") }}
      />

      {/* Em questão composta, render é por item. Cada item renderiza seu
          próprio bloco (MC com alternativas ou Essay com gabarito/critérios),
          empilhados na ordem. Caso/imagens/tabelas/fragmentos já apareceram
          acima e são compartilhados. */}
      {Array.isArray(q.items) && q.items.length >= 2 ? (
        <ol class="space-y-4">
          {q.items.map((it) => (
            <li key={it.id} class="rounded-md border border-border bg-background/40 p-4">
              <header class="mb-2 flex items-center gap-2">
                <span
                  class={`grid size-7 shrink-0 place-items-center rounded-md text-xs font-bold ${
                    it.tipo === "essay"
                      ? "bg-violet-500/20 text-violet-700 dark:text-violet-300"
                      : "bg-blue-500/20 text-blue-700 dark:text-blue-300"
                  }`}
                >
                  {it.label}
                </span>
                <span
                  class={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                    it.tipo === "essay"
                      ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                      : "bg-blue-500/15 text-blue-700 dark:text-blue-300"
                  }`}
                >
                  {it.tipo === "essay" ? "Dissertativa" : "Múltipla escolha"}
                </span>
                {typeof it.notaQuestao === "number" && it.notaQuestao > 0 && (
                  <span class="text-[10px] text-muted-foreground">
                    Peso {it.notaQuestao}
                  </span>
                )}
              </header>
              <div
                class="prose-question mb-3 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: previewBlock(it.pergunta || "(sem comando)")
                }}
              />
              {it.tipo === "essay" ? (
                <PreviewItemEssay it={it} />
              ) : (
                <PreviewItemMc it={it} />
              )}
            </li>
          ))}
        </ol>
      ) : isEssay ? (
        <PreviewEssay q={q} />
      ) : (
        <PreviewAlternativas q={q} />
      )}
    </div>
  );
}

// Previa de um item dentro de questão composta. Mesma gramática visual da
// prévia de questão simples — só que aplicada ao item.
function PreviewItemMc({ it }: { it: EditItem }) {
  const opcoes = it.opcoes ?? [];
  const anulada = it.correta === 0;
  const corrIdx = !anulada && typeof it.correta === "number" ? it.correta - 1 : -1;
  return (
    <ul class="space-y-2">
      {opcoes.map((opt, i) => {
        const letter = String.fromCharCode(65 + i);
        const isCorreta = i === corrIdx;
        const classes = isCorreta
          ? "border-emerald-500 bg-emerald-500/30 ring-2 ring-emerald-500/60 shadow-lg shadow-emerald-500/30"
          : "border-border bg-card/60";
        const badgeClasses = isCorreta
          ? "border-emerald-500 bg-emerald-500 text-white"
          : "border-border bg-background";
        return (
          <li key={i}>
            <div class={`flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left text-sm ${classes}`}>
              <span class={`grid size-7 shrink-0 place-items-center rounded-full border text-xs font-bold ${badgeClasses}`}>
                {letter}
              </span>
              <span
                class="flex-1 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: previewInline(opt || "—") }}
              />
              {isCorreta && (
                <Check className="size-5 shrink-0 text-emerald-500" strokeWidth={3} />
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function PreviewItemEssay({ it }: { it: EditItem }) {
  return (
    <div class="space-y-3">
      <div class="rounded-md border border-dashed border-border bg-card/40 px-4 py-4 text-center">
        <PenLine className="mx-auto mb-1 size-4 text-violet-500" />
        <p class="text-xs font-bold">Resposta dissertativa do item</p>
      </div>
      {it.gabarito_modelo && (
        <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-3 py-2 text-sm">
          <div class="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Info className="size-3" />
            Gabarito-modelo do item
          </div>
          <div
            class="prose-question leading-relaxed"
            dangerouslySetInnerHTML={{ __html: previewBlock(it.gabarito_modelo) }}
          />
        </div>
      )}
    </div>
  );
}

function PreviewAlternativas({ q }: { q: EditQuestao }) {
  const opcoes = q.opcoes ?? [];
  const anulada = q.correta === 0;
  // Em prévia, simulamos que o aluno marcou a correta — destaca em verde
  // exatamente como aparece no Modo Estudo após acertar (mesmas classes de
  // QuestionCard isSelected+acertou). Em anulada, não há "correta" visível.
  const corrIdx = !anulada && typeof q.correta === "number" ? q.correta - 1 : -1;
  return (
    <ul class="space-y-2">
      {opcoes.map((opt, i) => {
        const letter = String.fromCharCode(65 + i);
        const isCorreta = i === corrIdx;
        const classes = isCorreta
          ? "border-emerald-500 bg-emerald-500/30 ring-2 ring-emerald-500/60 shadow-lg shadow-emerald-500/30"
          : "border-border bg-card/60";
        const badgeClasses = isCorreta
          ? "border-emerald-500 bg-emerald-500 text-white"
          : "border-border bg-background";
        return (
          <li key={i}>
            <div
              class={`flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left text-sm ${classes}`}
            >
              <span
                class={`grid size-7 shrink-0 place-items-center rounded-full border text-xs font-bold ${badgeClasses}`}
              >
                {letter}
              </span>
              <span
                class="flex-1 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: previewInline(opt || "—") }}
              />
              {isCorreta && (
                <Check className="size-5 shrink-0 text-emerald-500" strokeWidth={3} />
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function PreviewEssay({ q }: { q: EditQuestao }) {
  const fragmentos = Array.isArray(q.fragmentos) ? q.fragmentos : [];
  const criterios = Array.isArray(q.criterios) ? q.criterios : [];
  const competencias = Array.isArray(q.competencias) ? q.competencias : [];
  return (
    <div class="space-y-4">
      {/* Linha de metadados da banca */}
      {(q.banca || q.genero || q.linhas_min || q.linhas_max || q.nota_maxima) && (
        <div class="flex flex-wrap items-center gap-2 text-[11px]">
          {q.banca && (
            <span class="rounded-md bg-violet-500/15 px-2 py-0.5 font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
              {q.banca}
            </span>
          )}
          {q.genero && (
            <span class="rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground">
              {q.genero}
            </span>
          )}
          {(q.linhas_min || q.linhas_max) && (
            <span class="rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground">
              {q.linhas_min && q.linhas_max
                ? `${q.linhas_min}–${q.linhas_max} linhas`
                : q.linhas_max
                  ? `até ${q.linhas_max} linhas`
                  : `mín. ${q.linhas_min} linhas`}
            </span>
          )}
          {typeof q.nota_maxima === "number" && q.nota_maxima > 0 && (
            <span class="rounded-md border border-border bg-background px-2 py-0.5 text-muted-foreground">
              Nota máx. {q.nota_maxima}
            </span>
          )}
        </div>
      )}

      {fragmentos.length > 0 && (
        <div class="space-y-2">
          <div class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <FileText className="size-3" />
            Textos motivadores ({fragmentos.length})
          </div>
          <div class="space-y-2">
            {fragmentos.map((f, i) => (
              <div
                key={i}
                class="rounded-md border-l-4 border-violet-500/40 bg-violet-500/5 px-4 py-3"
              >
                {f.label && (
                  <div class="mb-1 text-[11px] font-bold text-violet-700 dark:text-violet-300">
                    {f.label}
                  </div>
                )}
                <div
                  class="prose-question text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: previewBlock(f.textContent ?? "") }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {q.direcionadores && (
        <div class="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
            <ListChecks className="size-3" />
            Instruções da proposta
          </div>
          <div
            class="prose-question text-sm leading-relaxed"
            dangerouslySetInnerHTML={{ __html: previewBlock(q.direcionadores) }}
          />
        </div>
      )}

      <div class="rounded-md border border-dashed border-border bg-card/40 px-4 py-6 text-center">
        <PenLine className="mx-auto mb-2 size-5 text-violet-500" />
        <p class="text-sm font-bold">Questão dissertativa</p>
        <p class="mt-1 text-xs text-muted-foreground">
          Aluno escreve a resposta aqui e recebe correção pela IA.
        </p>
      </div>

      {q.gabarito_modelo && (
        <div class="rounded-md border-l-4 border-primary/40 bg-accent/20 px-4 py-3 text-sm">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <Info className="size-3" />
            Gabarito-modelo
          </div>
          <div
            class="prose-question leading-relaxed"
            dangerouslySetInnerHTML={{ __html: previewBlock(q.gabarito_modelo) }}
          />
        </div>
      )}

      {(criterios.length > 0 || competencias.length > 0) && (
        <div class="rounded-md border border-border bg-card/40 px-4 py-3 text-sm">
          <div class="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <ListChecks className="size-3" />
            Critérios de correção
          </div>
          {criterios.length > 0 && (
            <ul class="ml-4 list-disc space-y-1 text-[13px] leading-snug">
              {criterios.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          )}
          {competencias.length > 0 && (
            <>
              <div class="mt-3 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Competências adicionais
              </div>
              <ul class="ml-4 list-disc space-y-1 text-[13px] leading-snug">
                {competencias.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PreviewTable({ t }: { t: any }) {
  const tab = t && typeof t === "object" ? (t as Record<string, unknown>) : {};
  const cab = Array.isArray(tab.cabecalho) ? (tab.cabecalho as string[]) : [];
  const lin = Array.isArray(tab.linhas) ? (tab.linhas as string[][]) : [];
  if (cab.length === 0 && lin.length === 0) return null;
  return (
    <figure class="overflow-x-auto rounded-md border border-border">
      {typeof tab.titulo === "string" && tab.titulo && (
        <figcaption class="border-b border-border bg-accent/40 px-3 py-1.5 text-xs font-bold">
          {tab.titulo}
        </figcaption>
      )}
      <table class="w-full text-xs">
        {cab.length > 0 && (
          <thead class="bg-accent/30">
            <tr>
              {cab.map((h, i) => (
                <th key={i} class="border-b border-r border-border px-2 py-1 text-left font-bold last:border-r-0">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {lin.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c} class="border-b border-r border-border px-2 py-1 last:border-r-0">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {typeof tab.nota === "string" && tab.nota && (
        <p class="border-t border-border bg-card/60 px-3 py-1 text-[10px] italic text-muted-foreground">
          {tab.nota}
        </p>
      )}
    </figure>
  );
}

function PreviewDifficultyStars({ value }: { value: number }) {
  const n = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <span title={`Dificuldade ${n}/5`} class="inline-flex h-6 items-center gap-0.5 rounded bg-accent px-2 leading-none">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg
          key={i}
          width={12}
          height={12}
          viewBox="0 0 24 24"
          fill={i < n ? "#f59e0b" : "none"}
          stroke={i < n ? "#f59e0b" : "currentColor"}
          stroke-width="2"
          stroke-linejoin="round"
          stroke-linecap="round"
          class={i < n ? "" : "text-muted-foreground/50"}
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
      ))}
    </span>
  );
}

// Mini renderer Markdown-like alinhado com o do Modo Estudo: **bold**,
// *italic*, `code`, e quebras de linha simples. Sem KaTeX (placeholder
// pra quando extrairmos os helpers de `student-new/StudyWorkspace`).
function previewInline(raw: string): string {
  let s = String(raw ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
  s = s.replace(/`([^`]+)`/g, '<code class="rounded bg-accent px-1 py-0.5 text-[0.92em]">$1</code>');
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, "$1<em>$2</em>");
  return s;
}
function previewBlock(raw: string): string {
  const paragraphs = String(raw ?? "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
  return paragraphs
    .map((p) => {
      const lines = p.split("\n").map(previewInline).join("<br>");
      return `<p>${lines}</p>`;
    })
    .join("");
}

// ============================================================================
// Seção "Itens" — questão composta (a/b/c sob mesmo caso)
// ============================================================================

function CompositeToggle({ q, idx }: { q: EditQuestao; idx: number }) {
  return (
    <Section title="Questão com múltiplas partes (a/b/c)?">
      <button
        type="button"
        onClick={() => promoteToComposite(idx)}
        class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs font-bold text-primary hover:bg-primary/10"
      >
        <Plus className="size-3.5" />
        Tornar questão composta (a/b/c...)
      </button>
      <p class="mt-1 text-[11px] text-muted-foreground">
        Use quando o mesmo caso/texto/imagem é seguido por mais de uma pergunta. Os
        campos atuais viram o <strong>item a)</strong> e abriremos um <strong>item b)</strong>{" "}
        em branco pra você preencher. O caso, imagens e tabelas seguem compartilhados.
      </p>
    </Section>
  );
}

function ItemsSection({ q, idx }: { q: EditQuestao; idx: number }) {
  const items = q.items ?? [];
  return (
    <Section title={`Itens (${items.length})`}>
      <p class="mb-3 text-[11px] text-muted-foreground">
        Cada item é uma sub-questão com seu próprio comando, tipo (ME ou Dissertativa),
        gabarito e peso. O caso, imagens, tabelas e fragmentos acima são compartilhados
        por todos os itens — não duplique aqui.
      </p>
      <ul class="space-y-3">
        {items.map((it, j) => (
          <li key={it.id}>
            <ItemEditCard
              it={it}
              qIdx={idx}
              itemIdx={j}
              total={items.length}
            />
          </li>
        ))}
      </ul>
      <div class="mt-3 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => addItem(idx)}
          class="inline-flex items-center gap-1.5 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs font-bold text-primary hover:bg-primary/10"
        >
          <Plus className="size-3.5" /> Adicionar item
        </button>
        {items.length === 1 && (
          <button
            type="button"
            onClick={() => demoteToSimple(idx)}
            class="text-[11px] font-medium text-muted-foreground hover:text-foreground hover:underline"
          >
            Reverter pra questão simples
          </button>
        )}
      </div>
    </Section>
  );
}

function ItemEditCard({
  it,
  qIdx,
  itemIdx,
  total
}: {
  it: EditItem;
  qIdx: number;
  itemIdx: number;
  total: number;
}) {
  const kind: QuestionKind = it.tipo === "essay" ? "essay" : "mc";
  function patch(p: Partial<EditItem>) {
    updateItem(qIdx, itemIdx, p);
  }
  const accent =
    kind === "essay"
      ? "border-violet-500/40 bg-violet-500/5"
      : "border-blue-500/40 bg-blue-500/5";
  const labelBadge =
    kind === "essay"
      ? "bg-violet-500/20 text-violet-700 dark:text-violet-300"
      : "bg-blue-500/20 text-blue-700 dark:text-blue-300";
  return (
    <article class={`space-y-3 rounded-md border p-3 ${accent}`}>
      {/* Header do item: label editável + tipo + nota + move/remove */}
      <header class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex flex-wrap items-center gap-2">
          <span class={`grid size-7 shrink-0 place-items-center rounded-md text-xs font-bold ${labelBadge}`}>
            <input
              type="text"
              value={it.label}
              onInput={(e) => patch({ label: (e.target as HTMLInputElement).value.slice(0, 4) })}
              maxLength={4}
              aria-label="Rótulo do item"
              class="w-6 bg-transparent text-center outline-none"
            />
          </span>
          <span class="text-xs font-bold text-muted-foreground">
            Item {itemIdx + 1} de {total}
          </span>
          <KindToggle current={kind} onChange={(k) => changeItemKind(qIdx, itemIdx, k)} />
          <label class="inline-flex items-center gap-1 text-[11px]">
            <span class="text-muted-foreground">Peso</span>
            <input
              type="number"
              value={String(it.notaQuestao ?? "")}
              onInput={(e) => {
                const v = (e.target as HTMLInputElement).value;
                patch({ notaQuestao: v === "" ? undefined : Number(v) });
              }}
              min={0}
              max={1000}
              step="0.1"
              placeholder="—"
              title="Peso do item na prova agendada"
              class="h-7 w-16 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-primary"
            />
          </label>
        </div>
        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={() => moveItem(qIdx, itemIdx, -1)}
            disabled={itemIdx === 0}
            title="Mover pra cima"
            aria-label="Mover item pra cima"
            class="inline-flex size-7 items-center justify-center rounded-md border border-border bg-background hover:bg-accent disabled:opacity-40"
          >
            <ChevronUp className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => moveItem(qIdx, itemIdx, 1)}
            disabled={itemIdx === total - 1}
            title="Mover pra baixo"
            aria-label="Mover item pra baixo"
            class="inline-flex size-7 items-center justify-center rounded-md border border-border bg-background hover:bg-accent disabled:opacity-40"
          >
            <ChevronDown className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => void removeItem(qIdx, itemIdx)}
            title="Remover item"
            aria-label="Remover item"
            class="inline-flex size-7 items-center justify-center rounded-md border border-red-500/40 bg-red-500/5 text-red-600 hover:bg-red-500/10 dark:text-red-400"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </header>

      {/* Pergunta do item */}
      <Field label="Comando do item">
        <textarea
          value={it.pergunta}
          onInput={(e) => patch({ pergunta: (e.target as HTMLTextAreaElement).value })}
          rows={2}
          class={textareaClass}
          placeholder="Comando específico do item (sem repetir o caso compartilhado)"
        />
      </Field>

      {/* Corpo específico do tipo do item — reusa McItemFields/EssayItemFields */}
      {kind === "mc" ? (
        <McItemFields it={it} qIdx={qIdx} itemIdx={itemIdx} />
      ) : (
        <EssayItemFields it={it} qIdx={qIdx} itemIdx={itemIdx} />
      )}
    </article>
  );
}

// Versões "leves" dos campos MC/Essay aplicadas a um EditItem. Reúsa as
// estruturas dos componentes McFields/EssayFields, mas mapeando pra patch
// de item em vez de patch de questão.
function McItemFields({
  it,
  qIdx,
  itemIdx
}: {
  it: EditItem;
  qIdx: number;
  itemIdx: number;
}) {
  function patch(p: Partial<EditItem>) {
    updateItem(qIdx, itemIdx, p);
  }
  const opcoes = it.opcoes ?? [];
  const anulada = it.correta === 0;
  function setOpcao(i: number, valor: string) {
    const novas = [...opcoes];
    novas[i] = valor;
    patch({ opcoes: novas });
  }
  function addOpcao() {
    if (opcoes.length >= 8) return;
    patch({ opcoes: [...opcoes, ""] });
  }
  function removeOpcao(i: number) {
    if (opcoes.length <= 2) return;
    const novas = opcoes.filter((_, j) => j !== i);
    patch({ opcoes: novas });
    const cor = it.correta ?? 1;
    if (cor === i + 1) patch({ correta: 1 });
    else if (cor > i + 1) patch({ correta: cor - 1 });
  }
  return (
    <div class="space-y-2">
      <div class="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background/60 px-3 py-2">
        <label class="flex cursor-pointer items-center gap-2 text-xs">
          <Switch
            checked={anulada}
            onCheckedChange={(v) => patch({ correta: v ? 0 : 1 })}
          />
          <span class="font-bold">Item anulado</span>
        </label>
      </div>
      <ul class={`space-y-1.5 ${anulada ? "opacity-60" : ""}`}>
        {opcoes.map((opt, i) => {
          const letter = String.fromCharCode(65 + i);
          const isCorreta = !anulada && it.correta === i + 1;
          return (
            <li key={i} class="flex items-start gap-2">
              <button
                type="button"
                disabled={anulada}
                onClick={() => patch({ correta: i + 1 })}
                title={isCorreta ? "Esta é a correta" : "Marcar como correta"}
                class={`mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full border-2 text-[11px] font-bold transition-colors disabled:cursor-not-allowed ${
                  isCorreta
                    ? "border-emerald-500 bg-emerald-500 text-white"
                    : "border-border bg-background text-muted-foreground hover:border-emerald-500/60"
                }`}
              >
                {letter}
              </button>
              <textarea
                value={opt}
                onInput={(e) => setOpcao(i, (e.target as HTMLTextAreaElement).value)}
                rows={Math.max(1, Math.ceil((opt.length || 1) / 80))}
                class={`${textareaClass} flex-1`}
                placeholder={`Alternativa ${letter}`}
              />
              <button
                type="button"
                onClick={() => removeOpcao(i)}
                disabled={opcoes.length <= 2}
                title="Remover alternativa"
                aria-label="Remover alternativa"
                class="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent disabled:opacity-30"
              >
                <X className="size-3" />
              </button>
            </li>
          );
        })}
      </ul>
      {opcoes.length < 8 && (
        <button
          type="button"
          onClick={addOpcao}
          class="inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-background px-2 py-1 text-[11px] font-medium hover:bg-accent"
        >
          <Plus className="size-3" /> Adicionar alternativa
        </button>
      )}
      <Field label="Explicação do item">
        <textarea
          value={it.explicacao ?? ""}
          onInput={(e) => patch({ explicacao: (e.target as HTMLTextAreaElement).value })}
          rows={3}
          class={textareaClass}
          placeholder="Resolução do item — por que a marcada é correta e as outras estão erradas."
        />
      </Field>
    </div>
  );
}

function EssayItemFields({
  it,
  qIdx,
  itemIdx
}: {
  it: EditItem;
  qIdx: number;
  itemIdx: number;
}) {
  function patch(p: Partial<EditItem>) {
    updateItem(qIdx, itemIdx, p);
  }
  const criterios = it.criterios ?? [];
  function setCrit(i: number, v: string) {
    patch({ criterios: criterios.map((c, j) => (j === i ? v : c)) });
  }
  function addCrit() {
    patch({ criterios: [...criterios, ""] });
  }
  function removeCrit(i: number) {
    patch({ criterios: criterios.filter((_, j) => j !== i) });
  }
  return (
    <div class="space-y-3">
      <div class="grid gap-2 sm:grid-cols-3">
        <Field label="Linhas mín.">
          <input
            type="number"
            title="Linhas mínimas exigidas no item"
            placeholder="0"
            value={String(it.linhas_min ?? 0)}
            onInput={(e) =>
              patch({ linhas_min: Number((e.target as HTMLInputElement).value) || 0 })
            }
            min={0}
            max={100}
            class={inputClass}
          />
        </Field>
        <Field label="Linhas máx.">
          <input
            type="number"
            title="Linhas máximas permitidas no item"
            placeholder="0"
            value={String(it.linhas_max ?? 0)}
            onInput={(e) =>
              patch({ linhas_max: Number((e.target as HTMLInputElement).value) || 0 })
            }
            min={0}
            max={200}
            class={inputClass}
          />
        </Field>
        <Field label="Nota máx. da banca">
          <input
            type="number"
            title="Nota máxima da banca para este item"
            placeholder="10"
            value={String(it.nota_maxima ?? 0)}
            onInput={(e) =>
              patch({ nota_maxima: Number((e.target as HTMLInputElement).value) || 0 })
            }
            min={0}
            max={1000}
            class={inputClass}
          />
        </Field>
      </div>
      <Field label="Gabarito-modelo do item">
        <textarea
          value={it.gabarito_modelo ?? ""}
          onInput={(e) =>
            patch({ gabarito_modelo: (e.target as HTMLTextAreaElement).value })
          }
          rows={4}
          class={textareaClass}
          placeholder="Esqueleto da resposta esperada — pode citar partes do caso."
        />
      </Field>
      <div>
        <span class="mb-1 block text-[11px] font-semibold text-muted-foreground">
          Critérios de correção
        </span>
        <ul class="space-y-1">
          {criterios.map((c, i) => (
            <li key={i} class="flex items-center gap-2">
              <input
                type="text"
                value={c}
                onInput={(e) => setCrit(i, (e.target as HTMLInputElement).value)}
                class={`${inputClass} flex-1`}
                placeholder={`Critério ${i + 1}`}
              />
              <button
                type="button"
                onClick={() => removeCrit(i)}
                aria-label="Remover critério"
                title="Remover critério"
                class="inline-flex size-6 items-center justify-center rounded-md border border-border bg-background hover:bg-accent"
              >
                <X className="size-3" />
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={addCrit}
          class="mt-1 inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-background px-2 py-1 text-[11px] font-medium hover:bg-accent"
        >
          <Plus className="size-3" /> Adicionar critério
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Toggle de tipo
// ============================================================================

function KindToggle({
  current,
  onChange
}: {
  current: QuestionKind;
  onChange: (k: QuestionKind) => void;
}) {
  return (
    <div class="inline-flex items-center overflow-hidden rounded-md border border-border bg-background text-[11px]">
      <button
        type="button"
        onClick={() => onChange("mc")}
        class={`h-8 px-2 font-bold ${
          current === "mc"
            ? "bg-blue-500/15 text-blue-700 dark:text-blue-300"
            : "text-muted-foreground hover:bg-accent"
        }`}
      >
        ME
      </button>
      <button
        type="button"
        onClick={() => onChange("essay")}
        class={`h-8 border-l border-border px-2 font-bold ${
          current === "essay"
            ? "bg-violet-500/15 text-violet-700 dark:text-violet-300"
            : "text-muted-foreground hover:bg-accent"
        }`}
      >
        Dissertativa
      </button>
    </div>
  );
}

// ============================================================================
// Campos exclusivos: MC
// ============================================================================

function McFields({
  q,
  onPatch
}: {
  q: EditQuestao;
  onPatch: (p: Partial<EditQuestao>) => void;
}) {
  const opcoes = q.opcoes ?? [];

  function setOpcao(i: number, valor: string) {
    const novas = [...opcoes];
    novas[i] = valor;
    onPatch({ opcoes: novas });
  }

  function addOpcao() {
    if (opcoes.length >= 8) return;
    onPatch({ opcoes: [...opcoes, ""] });
  }

  function removeOpcao(i: number) {
    if (opcoes.length <= 2) return;
    const novas = opcoes.filter((_, j) => j !== i);
    onPatch({ opcoes: novas });
    // Ajusta `correta` se a removida vier antes da correta
    const cor = q.correta ?? 1;
    if (cor === i + 1) onPatch({ correta: 1 });
    else if (cor > i + 1) onPatch({ correta: cor - 1 });
  }

  const anulada = q.correta === 0;
  return (
    <>
      <Section title="Alternativas">
        {/* Toggle de anulada — separado das alternativas pra ficar inequívoco
            que o valor salvo é correta=0. Espelha o badge "Anulada" que aluno
            vê no Modo Estudo/Prova. */}
        <div class="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-background/60 px-3 py-2">
          <label class="flex cursor-pointer items-center gap-2 text-xs">
            <Switch
              checked={anulada}
              onCheckedChange={(v) => onPatch({ correta: v ? 0 : 1 })}
            />
            <span class="font-bold">Questão anulada</span>
            <span class="text-muted-foreground">
              · qualquer resposta do aluno vale como certa
            </span>
          </label>
          {anulada && (
            <span class="rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
              Anulada
            </span>
          )}
        </div>
        <p class="mb-2 text-[11px] text-muted-foreground">
          {anulada
            ? "Mesmo anulada, mantenha as alternativas pra preservar o histórico da prova original."
            : "Clique no rádio à esquerda pra marcar a alternativa correta."}
        </p>
        <ul class={`space-y-2 ${anulada ? "opacity-60" : ""}`}>
          {opcoes.map((opt, i) => {
            const letter = String.fromCharCode(65 + i);
            const isCorreta = !anulada && q.correta === i + 1;
            return (
              <li key={i} class="flex items-start gap-2">
                <button
                  type="button"
                  disabled={anulada}
                  onClick={() => onPatch({ correta: i + 1 })}
                  title={isCorreta ? "Esta é a correta" : "Marcar como correta"}
                  class={`mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors disabled:cursor-not-allowed ${
                    isCorreta
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : "border-border bg-background text-muted-foreground hover:border-emerald-500/60"
                  }`}
                >
                  {letter}
                </button>
                <div class="flex-1">
                  <MdField
                    value={opt}
                    onChange={(v) => setOpcao(i, v)}
                    variant="inline"
                    rows={Math.max(1, Math.ceil((opt.length || 1) / 80))}
                    placeholder={`Alternativa ${letter}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeOpcao(i)}
                  disabled={opcoes.length <= 2}
                  title="Remover alternativa"
                  class="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent disabled:opacity-30"
                >
                  <X className="size-3.5" />
                </button>
              </li>
            );
          })}
        </ul>
        {opcoes.length < 8 && (
          <button
            type="button"
            onClick={addOpcao}
            class="mt-2 inline-flex items-center gap-1.5 rounded-md border border-dashed border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
          >
            <Plus className="size-3.5" /> Adicionar alternativa
          </button>
        )}
      </Section>

      <Section title="Explicação / resolução">
        <MdField
          value={q.explicacao ?? ""}
          onChange={(v) => onPatch({ explicacao: v })}
          variant="rich"
          rows={6}
          placeholder="Resolução defendendo a correta + por que as outras estão erradas. Suporta Markdown e LaTeX ($...$ inline, $$...$$ display)."
        />
      </Section>
    </>
  );
}

// ============================================================================
// Campos exclusivos: Essay
// ============================================================================

function EssayFields({
  q,
  onPatch
}: {
  q: EditQuestao;
  onPatch: (p: Partial<EditQuestao>) => void;
}) {
  const fragmentos = q.fragmentos ?? [];
  const criterios = q.criterios ?? [];
  const competencias = q.competencias ?? [];

  function setFragLabel(i: number, v: string) {
    const novos = fragmentos.map((f, j) => (j === i ? { ...f, label: v } : f));
    onPatch({ fragmentos: novos });
  }
  function setFragText(i: number, v: string) {
    const novos = fragmentos.map((f, j) => (j === i ? { ...f, textContent: v } : f));
    onPatch({ fragmentos: novos });
  }
  function addFrag() {
    onPatch({
      fragmentos: [...fragmentos, { label: `Texto ${fragmentos.length + 1}`, textContent: "" }]
    });
  }
  function removeFrag(i: number) {
    onPatch({ fragmentos: fragmentos.filter((_, j) => j !== i) });
  }
  function setListItem(
    field: "criterios" | "competencias",
    list: string[],
    i: number,
    v: string
  ) {
    const novos = list.map((x, j) => (j === i ? v : x));
    onPatch({ [field]: novos });
  }
  function addListItem(field: "criterios" | "competencias", list: string[]) {
    onPatch({ [field]: [...list, ""] });
  }
  function removeListItem(field: "criterios" | "competencias", list: string[], i: number) {
    onPatch({ [field]: list.filter((_, j) => j !== i) });
  }

  return (
    <>
      <Section title="Banca e formato">
        <div class="grid gap-3 sm:grid-cols-2">
          <Field label="Banca">
            <input
              type="text"
              value={q.banca ?? ""}
              onInput={(e) => onPatch({ banca: (e.target as HTMLInputElement).value })}
              class={inputClass}
              placeholder="ENEM, FUVEST, UNICAMP, UNESP, ITA…"
            />
          </Field>
          <Field label="Gênero textual">
            <input
              type="text"
              value={q.genero ?? ""}
              onInput={(e) => onPatch({ genero: (e.target as HTMLInputElement).value })}
              class={inputClass}
              placeholder="Dissertativo-argumentativo, Carta, Artigo de opinião…"
            />
          </Field>
          <Field label="Linhas mín.">
            <input
              type="number"
              title="Linhas mínimas exigidas"
              placeholder="ex: 7"
              value={String(q.linhas_min ?? 0)}
              onInput={(e) =>
                onPatch({ linhas_min: Number((e.target as HTMLInputElement).value) || 0 })
              }
              min={0}
              max={100}
              class={inputClass}
            />
          </Field>
          <Field label="Linhas máx.">
            <input
              type="number"
              title="Linhas máximas permitidas"
              placeholder="ex: 30"
              value={String(q.linhas_max ?? 0)}
              onInput={(e) =>
                onPatch({ linhas_max: Number((e.target as HTMLInputElement).value) || 0 })
              }
              min={0}
              max={200}
              class={inputClass}
            />
          </Field>
          <Field label="Nota máxima da banca">
            <input
              type="number"
              title="Nota máxima da banca"
              placeholder="ex: 1000 (ENEM), 10"
              value={String(q.nota_maxima ?? 0)}
              onInput={(e) =>
                onPatch({ nota_maxima: Number((e.target as HTMLInputElement).value) || 0 })
              }
              min={0}
              max={1000}
              class={inputClass}
            />
          </Field>
        </div>
      </Section>

      <Section title="Fragmentos de apoio (textos motivadores)">
        <p class="mb-2 text-[11px] text-muted-foreground">
          Cada fragmento aparece como bloco lateral pro aluno. Use o rótulo pra identificar a
          fonte (ex.: "Texto I — IBGE 2023").
        </p>
        <ul class="space-y-3">
          {fragmentos.map((f, i) => (
            <li
              key={i}
              class="space-y-2 rounded-md border border-violet-500/30 bg-violet-500/5 p-3"
            >
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  value={f.label}
                  onInput={(e) => setFragLabel(i, (e.target as HTMLInputElement).value)}
                  class={`${inputClass} flex-1`}
                  placeholder={`Texto ${i + 1} — fonte`}
                />
                <button
                  type="button"
                  onClick={() => removeFrag(i)}
                  title="Remover fragmento"
                  class="inline-flex size-7 items-center justify-center rounded-md border border-border bg-background hover:bg-accent"
                >
                  <X className="size-3.5" />
                </button>
              </div>
              <textarea
                value={f.textContent}
                onInput={(e) => setFragText(i, (e.target as HTMLTextAreaElement).value)}
                rows={4}
                class={textareaClass}
                placeholder="Conteúdo do fragmento (entre 200 e 800 caracteres é o ideal)…"
              />
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={addFrag}
          class="mt-2 inline-flex items-center gap-1.5 rounded-md border border-dashed border-violet-500/40 bg-violet-500/5 px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
        >
          <Plus className="size-3.5" /> Adicionar fragmento
        </button>
      </Section>

      <Section title="Direcionadores (instruções da proposta)">
        <textarea
          value={q.direcionadores ?? ""}
          onInput={(e) =>
            onPatch({ direcionadores: (e.target as HTMLTextAreaElement).value })
          }
          rows={4}
          class={textareaClass}
          placeholder="Ex: Apresentar tese clara; usar dados estatísticos; propor intervenção respeitando os direitos humanos."
        />
      </Section>

      <Section title="Gabarito-modelo">
        <textarea
          value={q.gabarito_modelo ?? ""}
          onInput={(e) =>
            onPatch({ gabarito_modelo: (e.target as HTMLTextAreaElement).value })
          }
          rows={6}
          class={textareaClass}
          placeholder="Esqueleto da redação esperada — tese, argumentos centrais e proposta. Suporta Markdown."
        />
      </Section>

      <Section title="Critérios de correção">
        <p class="mb-2 text-[11px] text-muted-foreground">
          Para ENEM, use as 5 competências. Para outras bancas, espelhe a grade declarada.
        </p>
        <ul class="space-y-2">
          {criterios.map((c, i) => (
            <li key={i} class="flex items-center gap-2">
              <input
                type="text"
                value={c}
                onInput={(e) =>
                  setListItem("criterios", criterios, i, (e.target as HTMLInputElement).value)
                }
                class={`${inputClass} flex-1`}
                placeholder={`Critério ${i + 1}`}
              />
              <button
                type="button"
                onClick={() => removeListItem("criterios", criterios, i)}
                title="Remover critério"
                class="inline-flex size-7 items-center justify-center rounded-md border border-border bg-background hover:bg-accent"
              >
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => addListItem("criterios", criterios)}
          class="mt-2 inline-flex items-center gap-1.5 rounded-md border border-dashed border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <Plus className="size-3.5" /> Adicionar critério
        </button>
      </Section>

      <Section title="Competências adicionais (opcional)">
        <ul class="space-y-2">
          {competencias.map((c, i) => (
            <li key={i} class="flex items-center gap-2">
              <input
                type="text"
                value={c}
                onInput={(e) =>
                  setListItem(
                    "competencias",
                    competencias,
                    i,
                    (e.target as HTMLInputElement).value
                  )
                }
                class={`${inputClass} flex-1`}
                placeholder={`Competência ${i + 1}`}
              />
              <button
                type="button"
                onClick={() => removeListItem("competencias", competencias, i)}
                title="Remover competência"
                class="inline-flex size-7 items-center justify-center rounded-md border border-border bg-background hover:bg-accent"
              >
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => addListItem("competencias", competencias)}
          class="mt-2 inline-flex items-center gap-1.5 rounded-md border border-dashed border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <Plus className="size-3.5" /> Adicionar competência
        </button>
      </Section>
    </>
  );
}

// ============================================================================
// Imagens — gestão básica de URL/base64 com preview
// ============================================================================

function ImagensSection({
  q,
  onPatch
}: {
  q: EditQuestao;
  onPatch: (p: Partial<EditQuestao>) => void;
}) {
  const imgs = q.imagens ?? [];
  const fileRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  function getUrl(it: string | { url: string; legenda?: string }): string {
    return typeof it === "string" ? it : it.url;
  }
  function getLegenda(it: string | { url: string; legenda?: string }): string {
    return typeof it === "string" ? "" : it.legenda ?? "";
  }
  function setLegenda(i: number, valor: string) {
    const novas = imgs.map((it, j) => {
      if (j !== i) return it;
      return { url: getUrl(it), legenda: valor };
    });
    onPatch({ imagens: novas });
  }
  function removeImg(i: number) {
    onPatch({ imagens: imgs.filter((_, j) => j !== i) });
  }
  async function ingestFiles(files: FileList | File[]) {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (arr.length === 0) return;
    const datas = await Promise.all(arr.map(fileToDataUrl));
    onPatch({ imagens: [...imgs, ...datas] });
  }
  async function onFile(e: Event) {
    const t = e.target as HTMLInputElement;
    if (!t.files) return;
    await ingestFiles(t.files);
    if (fileRef.current) fileRef.current.value = "";
  }
  async function addUrl() {
    const url = window.prompt("URL ou data URL da imagem:") ?? "";
    if (!url.trim()) return;
    onPatch({ imagens: [...imgs, url.trim()] });
  }

  // Drag-and-drop: ouve no container inteiro. Mostra overlay quando arrasta
  // qualquer coisa (não dá pra inspecionar o type no `dragenter` em todos os
  // browsers — então mostramos overlay e filtramos no drop).
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    function onEnter(e: DragEvent) {
      if (!e.dataTransfer) return;
      e.preventDefault();
      setDragging(true);
    }
    function onOver(e: DragEvent) {
      e.preventDefault();
    }
    function onLeave(e: DragEvent) {
      // Só sai do estado se o cursor realmente saiu do elemento (não trocou
      // de filho). currentTarget é o container; relatedTarget é o destino.
      if (!el!.contains(e.relatedTarget as Node | null)) setDragging(false);
    }
    function onDrop(e: DragEvent) {
      e.preventDefault();
      setDragging(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) void ingestFiles(files);
    }
    el.addEventListener("dragenter", onEnter);
    el.addEventListener("dragover", onOver);
    el.addEventListener("dragleave", onLeave);
    el.addEventListener("drop", onDrop);
    return () => {
      el.removeEventListener("dragenter", onEnter);
      el.removeEventListener("dragover", onOver);
      el.removeEventListener("dragleave", onLeave);
      el.removeEventListener("drop", onDrop);
    };
  }, [imgs]);

  // Ctrl+V de imagem: ouvimos `paste` no document, mas filtramos pra
  // ingerir só quando o foco está dentro da nossa zona OU quando o usuário
  // colou explicitamente sobre o editor (não em outro input/textarea).
  // Print screen, clipboard manager, screenshot tools — todos disparam aqui.
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      // Não interceptamos se o foco está num textarea/input — usuário
      // pode estar colando texto na pergunta/alternativa.
      const active = document.activeElement as HTMLElement | null;
      if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA")) {
        return;
      }
      const items = e.clipboardData?.items;
      if (!items) return;
      const arquivos: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === "file" && it.type.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) arquivos.push(f);
        }
      }
      if (arquivos.length > 0) {
        e.preventDefault();
        void ingestFiles(arquivos);
      }
    }
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [imgs]);

  return (
    <Section title="Imagens">
      <div
        ref={dropRef}
        class={`relative rounded-md border-2 border-dashed transition-colors ${
          dragging
            ? "border-primary bg-primary/5"
            : "border-border bg-background/40 hover:border-primary/40"
        }`}
      >
        {dragging && (
          <div class="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-md bg-primary/10">
            <div class="flex items-center gap-2 rounded-md bg-card px-4 py-2 text-sm font-bold shadow-lg">
              <ImageIcon className="size-4 text-primary" />
              Solte a imagem aqui
            </div>
          </div>
        )}
        <div class="p-3">
          {imgs.length > 0 ? (
            <ul class="grid gap-3 sm:grid-cols-2">
              {imgs.map((it, i) => (
                <li
                  key={i}
                  class="overflow-hidden rounded-md border border-border bg-background"
                >
                  <img
                    src={getUrl(it)}
                    alt={getLegenda(it) || `Imagem ${i + 1}`}
                    class="block max-h-48 w-full bg-accent/30 object-contain"
                    loading="lazy"
                  />
                  <div class="space-y-1 border-t border-border p-2">
                    <input
                      type="text"
                      value={getLegenda(it)}
                      onInput={(e) => setLegenda(i, (e.target as HTMLInputElement).value)}
                      class={`${inputClass} text-xs`}
                      placeholder="Legenda (opcional)"
                    />
                    <button
                      type="button"
                      onClick={() => removeImg(i)}
                      class="inline-flex items-center gap-1 text-[11px] font-medium text-red-600 hover:underline dark:text-red-400"
                    >
                      <Trash2 className="size-3" /> Remover
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div class="grid place-items-center px-3 py-6 text-center">
              <ImageIcon className="mb-2 size-6 text-muted-foreground" />
              <p class="text-xs font-bold">Sem imagens ainda</p>
              <p class="mt-0.5 text-[11px] text-muted-foreground">
                Arraste e solte aqui, cole com <kbd class="rounded border border-border bg-background px-1 font-mono">Ctrl</kbd>+<kbd class="rounded border border-border bg-background px-1 font-mono">V</kbd> ou use os botões abaixo.
              </p>
            </div>
          )}
        </div>
      </div>
      <div class="mt-2 flex items-center gap-2">
        <label class="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent">
          <ImageIcon className="size-3.5" /> Anexar arquivo
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            onChange={onFile}
          />
        </label>
        <button
          type="button"
          onClick={() => void addUrl()}
          class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
        >
          <Plus className="size-3.5" /> Adicionar por URL
        </button>
      </div>
    </Section>
  );
}

// ============================================================================
// Tabelas — editor com grade real (cabeçalho + linhas × colunas)
// ============================================================================
//
// Antes a única forma de inserir tabela era colar markdown formatado. Agora
// o professor edita célula a célula. Ele pode adicionar quantas tabelas
// quiser (cada uma com título e nota), e dentro de cada tabela controlar
// número de linhas e colunas via botões + e −.

interface EditTabela {
  titulo?: string;
  cabecalho: string[];
  linhas: string[][];
  nota?: string;
}

function TabelasSection({
  q,
  onPatch
}: {
  q: EditQuestao;
  onPatch: (p: Partial<EditQuestao>) => void;
}) {
  // Normaliza pra garantir shape; payloads antigos podem ter linhas
  // com tamanhos diferentes ou cabecalho ausente.
  const tabelas: EditTabela[] = (q.tabelas ?? []).map((t) => normalizeTabela(t));

  function update(i: number, novaTabela: EditTabela) {
    const novas = tabelas.map((t, j) => (j === i ? novaTabela : t));
    onPatch({ tabelas: novas });
  }
  function addTabela() {
    onPatch({
      tabelas: [
        ...tabelas,
        {
          titulo: "",
          cabecalho: ["Coluna 1", "Coluna 2"],
          linhas: [
            ["", ""],
            ["", ""]
          ],
          nota: ""
        }
      ]
    });
  }
  async function removeTabela(i: number) {
    const ok = await openConfirm({
      title: "Remover tabela?",
      message: "A tabela inteira será removida desta questão.",
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    onPatch({ tabelas: tabelas.filter((_, j) => j !== i) });
  }

  return (
    <Section title="Tabelas">
      {tabelas.length === 0 ? (
        <p class="mb-2 text-[11px] text-muted-foreground">
          Nenhuma tabela. Clique em <strong>Adicionar tabela</strong> pra criar uma grade
          editável de linhas e colunas.
        </p>
      ) : (
        <ul class="space-y-4">
          {tabelas.map((t, i) => (
            <li
              key={i}
              class="space-y-2 rounded-md border border-border bg-background/40 p-3"
            >
              <TabelaEditor
                t={t}
                onChange={(novo) => update(i, novo)}
                onRemove={() => void removeTabela(i)}
              />
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={addTabela}
        class="mt-2 inline-flex items-center gap-1.5 rounded-md border border-dashed border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-accent"
      >
        <Plus className="size-3.5" /> Adicionar tabela
      </button>
    </Section>
  );
}

function normalizeTabela(t: unknown): EditTabela {
  const obj = (t ?? {}) as Record<string, unknown>;
  const cabecalho: string[] = Array.isArray(obj.cabecalho)
    ? (obj.cabecalho as unknown[]).map((c) => String(c ?? ""))
    : [];
  const linhasRaw: unknown[] = Array.isArray(obj.linhas) ? (obj.linhas as unknown[]) : [];
  const ncols = Math.max(1, cabecalho.length, ...linhasRaw.map((r) => (Array.isArray(r) ? r.length : 0)));
  // Garante cabeçalho com `ncols` colunas
  while (cabecalho.length < ncols) cabecalho.push("");
  const linhas: string[][] = linhasRaw.map((r) => {
    const arr = Array.isArray(r) ? (r as unknown[]).map((c) => String(c ?? "")) : [];
    while (arr.length < ncols) arr.push("");
    return arr.slice(0, ncols);
  });
  return {
    titulo: typeof obj.titulo === "string" ? obj.titulo : "",
    cabecalho: cabecalho.slice(0, ncols),
    linhas,
    nota: typeof obj.nota === "string" ? obj.nota : ""
  };
}

function TabelaEditor({
  t,
  onChange,
  onRemove
}: {
  t: EditTabela;
  onChange: (novo: EditTabela) => void;
  onRemove: () => void;
}) {
  const ncols = t.cabecalho.length;
  const nrows = t.linhas.length;

  function setTitulo(v: string) {
    onChange({ ...t, titulo: v });
  }
  function setNota(v: string) {
    onChange({ ...t, nota: v });
  }
  function setHeader(c: number, v: string) {
    const cab = t.cabecalho.map((h, i) => (i === c ? v : h));
    onChange({ ...t, cabecalho: cab });
  }
  function setCell(r: number, c: number, v: string) {
    const linhas = t.linhas.map((row, i) =>
      i === r ? row.map((cell, j) => (j === c ? v : cell)) : row
    );
    onChange({ ...t, linhas });
  }
  function addRow() {
    onChange({ ...t, linhas: [...t.linhas, Array(ncols).fill("")] });
  }
  function removeRow(r: number) {
    if (t.linhas.length <= 1) return;
    onChange({ ...t, linhas: t.linhas.filter((_, i) => i !== r) });
  }
  function addCol() {
    if (ncols >= 12) return;
    const cab = [...t.cabecalho, `Coluna ${ncols + 1}`];
    const linhas = t.linhas.map((row) => [...row, ""]);
    onChange({ ...t, cabecalho: cab, linhas });
  }
  function removeCol(c: number) {
    if (ncols <= 1) return;
    const cab = t.cabecalho.filter((_, i) => i !== c);
    const linhas = t.linhas.map((row) => row.filter((_, i) => i !== c));
    onChange({ ...t, cabecalho: cab, linhas });
  }

  return (
    <div class="space-y-2">
      {/* Linha de título + nota + remover */}
      <div class="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={t.titulo ?? ""}
          onInput={(e) => setTitulo((e.target as HTMLInputElement).value)}
          placeholder="Título da tabela (opcional)"
          class={`${inputClass} flex-1`}
        />
        <span class="text-[10px] text-muted-foreground tabular-nums">
          {nrows}×{ncols}
        </span>
        <button
          type="button"
          onClick={onRemove}
          title="Remover tabela inteira"
          class="inline-flex size-8 items-center justify-center rounded-md border border-red-500/40 bg-red-500/5 text-red-600 hover:bg-red-500/10 dark:text-red-400"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>

      {/* Grade da tabela: scroll horizontal quando muito larga */}
      <div class="overflow-x-auto rounded-md border border-border">
        <table class="w-full border-collapse text-xs">
          <thead class="bg-accent/40">
            <tr>
              {t.cabecalho.map((h, c) => (
                <th
                  key={c}
                  class="border-b border-r border-border p-0 last:border-r-0"
                >
                  <div class="flex items-center gap-1 px-1.5 py-1">
                    <input
                      type="text"
                      value={h}
                      onInput={(e) => setHeader(c, (e.target as HTMLInputElement).value)}
                      placeholder={`Col ${c + 1}`}
                      class="min-w-[5rem] flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-xs font-bold outline-none focus:border-primary focus:bg-background"
                    />
                    {ncols > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCol(c)}
                        title="Remover coluna"
                        class="grid size-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-red-500/10 hover:text-red-600"
                      >
                        <X className="size-3" />
                      </button>
                    )}
                  </div>
                </th>
              ))}
              <th class="w-8 bg-card" aria-label="Adicionar coluna">
                <button
                  type="button"
                  onClick={addCol}
                  disabled={ncols >= 12}
                  title="Adicionar coluna"
                  aria-label="Adicionar coluna"
                  class="grid size-7 place-items-center text-primary hover:bg-accent disabled:opacity-30"
                >
                  <Plus className="size-3.5" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {t.linhas.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td
                    key={c}
                    class="border-b border-r border-border p-0 last:border-r-0"
                  >
                    <input
                      type="text"
                      value={cell}
                      onInput={(e) => setCell(r, c, (e.target as HTMLInputElement).value)}
                      placeholder="—"
                      class="w-full min-w-[5rem] border-none bg-transparent px-2 py-1.5 text-xs outline-none focus:bg-primary/5"
                    />
                  </td>
                ))}
                <td class="bg-card text-center">
                  {nrows > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(r)}
                      title="Remover linha"
                      class="grid size-7 place-items-center text-muted-foreground hover:bg-red-500/10 hover:text-red-600"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={ncols + 1} class="bg-card">
                <button
                  type="button"
                  onClick={addRow}
                  class="inline-flex h-7 w-full items-center justify-center gap-1 text-xs font-medium text-primary hover:bg-accent"
                >
                  <Plus className="size-3.5" /> Adicionar linha
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Nota/legenda da tabela */}
      <input
        type="text"
        value={t.nota ?? ""}
        onInput={(e) => setNota((e.target as HTMLInputElement).value)}
        placeholder="Fonte / nota da tabela (ex: Fonte: IBGE 2023)"
        class={inputClass}
      />
    </div>
  );
}

function fileToDataUrl(f: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(typeof fr.result === "string" ? fr.result : "");
    fr.onerror = () => reject(new Error("Falha ao ler arquivo"));
    fr.readAsDataURL(f);
  });
}

// ============================================================================
// Helpers visuais
// ============================================================================

const inputClass =
  "w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary";
const textareaClass =
  "w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm leading-relaxed outline-none focus:border-primary";

/** Wrapper que alterna entre <textarea> e <QuestionRichField> baseado em
 *  feature flag `teacher.question_editor.wysiwyg` (default OFF — admin liga
 *  no /manager/featureflags quando quiser testar). Mantém compat 100% com
 *  o caller — recebe markdown canônico em `value` e devolve markdown em
 *  `onChange`. [[project-question-wysiwyg-editor]] */
function MdField({
  value,
  onChange,
  variant = "block",
  rows = 3,
  placeholder,
  className
}: {
  value: string;
  onChange: (next: string) => void;
  variant?: "inline" | "block" | "rich";
  rows?: number;
  placeholder?: string;
  className?: string;
}) {
  const wysiwyg = flagEnabled("question_editor.wysiwyg", false);
  if (wysiwyg) {
    return (
      <QuestionRichField
        value={value}
        onChange={onChange}
        variant={variant}
        placeholder={placeholder}
        className={className}
      />
    );
  }
  return (
    <textarea
      value={value}
      onInput={(e) => onChange((e.target as HTMLTextAreaElement).value)}
      rows={rows}
      class={`${textareaClass} ${className ?? ""}`}
      placeholder={placeholder}
    />
  );
}

function Section({ title, children }: { title: string; children: any }) {
  return (
    <section class="space-y-2">
      <h3 class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[11px] font-semibold text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
